// 렌더 진입점. GameView를 읽기만 한다. WebGPU를 우선하고 지원하지 않거나 장치를 잃으면 WebGL2 백엔드로 대체한다.
// 두 백엔드에서 같은 코드가 돌도록 three/webgpu 노드 머티리얼과 TSL만 쓴다. 렌더 루프는 우리 runLoop가 부른다(setAnimationLoop 미사용).
import type { KaelTierState } from './kaelTier';
import * as THREE from 'three/webgpu';
import { installSkinnedNoAlloc } from './skinnedNoAlloc';
import { singlePassAdditive } from './singlePass';
import { BUDGET, MOB_STATS, PALETTE, PUSH_IN, type QualityLevel, type QualityStep } from '../shared/config';
import { Ev, type GameEvent } from '../shared/events';
import type { ScreenReserve } from '../ui/screenReserve';
import { isTouchDevice } from '../ui/touch';
import { MAPS, VILLAGES, rasterize, type MapDef } from '../shared/levels';
import { AwakenPhase, BossState, HeroAct, MobState, Scene, combatScene, type GameView } from '../shared/types';
import { FieldRender } from './field/index';
import { FieldMmoRender } from './field/mmo';
import { PartyPeersRender } from './field/party';
import { RaidPeersRender } from './field/raidPeers';
import { AwakenFx } from '../vfx/awaken';
import { EruptionFx } from '../vfx/eruption';
import { CombatFx } from '../vfx/combatFx';
import { DamageNumbers } from '../vfx/damageNumbers';
import { FlashLimiter } from '../vfx/flash';
import { KoScreen } from '../vfx/ko';
import { ParticleFx } from '../vfx/particles';
import { SlashFx, TRAIL_TICKS } from '../vfx/slashes';
import { VfxModules } from '../vfx/modules';
import { GearFx } from '../vfx/gearFx';
import { HitStateFx } from '../vfx/hitStateFx';
import { RegionMobFx } from '../vfx/regionMobFx';
import { SpecialFx } from '../vfx/specialFx';
import { BossRageFx } from '../vfx/bossRageFx';
import { KAEL_LOAD_TIMEOUT_MS, assetStatus } from './assets';
import { BossVisual } from './boss';
import { CameraRig, FIELD_BOSS_FRAME, TOUCH_BOSS_SHIFT } from './camera';
import { projectToScreen } from './screenProject';
import type { ShotId } from './cine';
import { GameClocks } from './clock';
import { pinShaderClock, type NodeFrameLike } from './shaderClock';
import { HeroVisual } from './hero';
import { AvatarLook } from './avatarLook';
import { KAEL_LAUNCHER_URL, KAEL_SCALE, KaelModel, LYRA_URL, loadExtraClips, loadKaelGltf } from './kael';
import { MercsVisual } from './actors/mercs';
import { RaidFx } from './raidFx';
import { HellFx } from './hellFx';
import { RaidVfx } from './raidVfx';
import { buildMap, CUT_H, cutFactor, setCutawayFocus, type MapVisual } from './map';
import { parseStage, stagePlan } from '../shared/content/stages';
import type { StageId } from '../shared/content/types';
import { decorChildren, findEnvRoot } from './env/decor';
import { stageEntryMap, stageMapsToWarm } from './env/warmMaps';
import { envProgress } from './env/kits';
import { placeLabels } from './env/label';
import { panelRects } from '../shared/panelRects';
import { MAP_MOOD as MOOD, moodOf } from './env/mood';
import { towerMoodOf, towerThemeKey } from './env/towerMood';
import { MobsVisual } from './mobs';
import { REGION_LOOK, type RegionLook } from './actors/regionAttach';
import { PostFx } from './post';
import { QUALITY_LADDER, QualityGovernor, RENDERER_SESSION_KEY, planQuality, readGpuInfo, shouldForceWebGL, type GovernorState, type GpuInfo, type QualityPlan } from './quality';
import { FirstFrameWatch, type ConsoleFn, type FallbackCause, fallbackReport, saveFallbackNote, takeFallbackNote, watchPipelineFailures } from './gpuFallback';
import { reportTelemetry } from '../telemetry/collector';
import { chooseBackend, guardWebGlFallback, planGpuRecovery, probeWebGl2, probeWebGpu, resolveCompatMode, StartupError, type BackendChoice, type ProbeCanvas } from './gpuProbe';
import { BlobShadows } from './shadows';
import { TelegraphVisual } from './telegraph';
import { AutoRouteFx } from './autoRouteFx';
import { BossPointer } from './bossPointer';
import { viewW, viewH } from '../shared/viewport';
import { LootFx } from './loot';
import { LootLabels } from './lootLabels';
import { LyraFx } from '../vfx/lyraFx';
import { LyraLink } from './lyraLink';
import { RootRings } from './rootRings';
import { RebirthFx } from './rebirthFx';
import { applyLyraLook, removeLyraLook, setLyraAim, setLyraDraw, type LyraLook } from './lyraLook';
import { loadWeaponGeometry, type WeaponTier } from './heroWeapons';
import { heroSkills } from '../shared/skills/registry';
/** 레이드 전투 구도(C8-5): 하한 거리(m, 기본 보스전 28)·피치 보정(도, 음수 = 낮게)·시선 높이(m, 키 9.9m 비룡의 몸 가운데 쪽) */
export const RAID_FRAME = { dist: 21, pitch: -4, lookY: 3 } as const;

/** 경고 투과선 가림 계산용 카메라 전방(미리 할당) */
const TELE_FWD = new THREE.Vector3();

export type BackendName = 'webgpu' | 'webgl2';

export interface PerfStats {
  /** 최근 프레임 시간 중앙값·90·95백분위(ms) */
  p50: number;
  p90: number;
  p95: number;
  /** 마지막 프레임 드로우콜(그림자·후처리 포함) */
  drawCalls: number;
  /** 마지막 프레임 삼각형 수(그림자·후처리 포함) */
  triangles: number;
  frames: number;
  /** 자동 품질 조절 여부와 현재 단계(고정 프리셋이면 step -1) */
  auto: boolean;
  level: QualityLevel;
  step: number;
  label: string;
  /** 조절기 목표 프레임 시간·판정 p90·변경 횟수 */
  targetMs: number;
  governorP90: number;
  changes: number;
  /** 조절기가 CPU 병목으로 판정했는지(해상도 0.85 단계를 쓰지 않는다) */
  cpuBound: boolean;
  /** 실제 해상도 배율, 저폴리로 그린 몹 수 */
  pixelRatio: number;
  lodMobs: number;
}

export interface RendererHandle {
  readonly kael: KaelTierState | null;
  readonly backend: BackendName;
  readonly quality: QualityLevel;
  /** 백엔드를 고른 이유와 시험 결과(진단용) */
  readonly choice: BackendChoice;
  readonly three: THREE.WebGPURenderer;
  /**
   * 한 프레임 진행·그리기. draw = false면 시계·이벤트·VFX·카메라 등 CPU 쪽 상태만 이 프레임만큼 진행하고 GPU 그리기는 건너뛴다
   * (결정적 틱 캡처가 중간 틱을 60fps와 같은 순서로 진행하되 느린 SwiftShader 그리기를 마지막 틱에만 하도록, c013 픽스처 전용)
   */
  render(alpha: number, frameDt: number, draw?: boolean): void;
  resize(): void;
  /** 마지막 프레임 드로우콜 수(성능 예산 확인용) */
  drawCalls(): number;
  /**
   * 단계 부팅에서 던전 자원이 아직 준비 중이면 안내 문구, 아니면 null.
   * 던전에 들어갔을 때 그레이박스나 빠진 몹이 보이지 않도록 호출 쪽이 대기 화면을 띄운다.
   */
  waiting(): string | null;
  /** 단계 부팅의 R1 던전 자원 준비 진행(0..1). 단계 부팅이 아니면 항상 1 */
  warmProgress(): number;
  /** 화면에 떠 있는 데미지 숫자 수(QA·진단용) */
  damageNumbers(): number;
  perf(): PerfStats;
  /** 섬광 리미터 통계(QA): 승인·차단 수 */
  flashes(): { granted: number; blocked: number };
  /** GPU 자원·오버레이 DOM·디버그 훅을 해제한다(생성의 역순). */
  dispose(): void;
}

export interface RendererOptions {
  forceWebGL?: boolean;
  /** 생략 또는 auto: ?quality → UI 설정 → 자동 품질 조절(기억된 단계 → GPU·화면 크기로 고른 첫 단계) */
  quality?: QualityLevel | 'auto';
  /** HUD 예약 영역(대상 프레임·보스 바). 피해 숫자가 이 영역을 비킨다. 같은 객체를 createUI에도 넘긴다 */
  reserve?: ScreenReserve;
  /** 실행 중 GPU 장치 유실이 자동 재시작 상한을 넘었을 때(안내 화면을 띄운다). 생략하면 콘솔에만 남긴다 */
  onFatal?: (err: StartupError) => void;
  /**
   * 단계 부팅(실사용자 경로): 마을 환경+Kael만 먼저 준비해 시작을 앞당기고, 던전 전용 몹·보스·VFX·던전 환경은
   * 준비된 뒤 뒤에서 읽고 미리 컴파일한다. 준비 전 프레임은 그리지 않는다(인트로 오버레이가 가린다). 생략하면 예전 순서(전부 컴파일 뒤 시작).
   */
  staged?: boolean;
}

// 맵별 분위기(하늘색·안개·광원·해 위치·블룸)는 env/mood.ts가 정한다.
const PERF_FRAMES = 300;

const makeProbeCanvas = (): ProbeCanvas => document.createElement('canvas');
/** 정책으로 저장소가 막히면 sessionStorage 속성 접근 자체가 SecurityError를 던진다 → null */
const sessionStore = (): Storage | null => { try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; } };

/** 호환 모드(WebGL2 우선) 여부. URL renderer=auto는 설정·세션 기록보다 우선한다(안내 화면의 '기본 모드로 다시 시도'). */
export function compatModeRequested(explicit?: boolean): boolean {
  const url = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('renderer') : null;
  return resolveCompatMode(url, explicit, () => shouldForceWebGL(false));
}

export async function createRenderer(canvas: HTMLCanvasElement, view: GameView, opts: RendererOptions = {}): Promise<RendererHandle> {
  // 스킨 정점 판독(발 접지 등)의 프레임마다 할당 제거(skinnedNoAlloc.ts)
  installSkinnedNoAlloc();
  // 로딩 구간 기록(QA): 시작 버튼까지의 대기를 구간별로 본다
  const loadT0 = performance.now();
  const loadTimes: Record<string, number> = {};
  const mark = (k: string): void => { loadTimes[k] = Math.round(performance.now() - loadT0); };
  const staged = opts.staged === true;
  // 진행률: 받기(0..0.45) → 마을 환경 컴파일·교체(+0.35) → Kael 부착(+0.19). 컴파일 구간은 길어서 받기만으로는 막대가 멈춰 보인다.
  const stage = { env: false, kael: false };
  let fetchP = 0;
  const pushProgress = (p?: number): void => {
    if (p !== undefined) fetchP = p;
    assetStatus.progress = Math.min(0.99, (fetchP * 0.7 + envProgress() * 0.3) * 0.45 + (stage.env ? 0.35 : 0) + (stage.kael ? 0.19 : 0));
  };
  // Kael GLB는 렌더러 준비·부팅 컴파일과 겹치도록 가장 먼저 받기 시작한다(예전에는 부팅 컴파일 뒤에 시작해 직렬이었다)
  const kaelGltf = loadKaelGltf((p) => pushProgress(p));
  const kaelRequestedAt = performance.now();
  kaelGltf.then(() => mark('kaelFetched'), () => mark('kaelFailed'));
  // 백엔드 선택: 게임 캔버스를 잡기 전에 임시 캔버스로 WebGPU 실사용 경로(한도·버퍼·파이프라인·화면 출력)를 시험하고,
  // 실패하면 WebGL2를 실제로 만들어 본다. 둘 다 안 되면 StartupError('no-gpu')로 안내 화면을 띄운다(gpuProbe.ts).
  const gpuApi = typeof navigator !== 'undefined' ? (navigator as Navigator & { gpu?: unknown }).gpu : undefined;
  // 지난 부팅이 WebGPU 실행 중 실패로 WebGL2로 다시 열렸으면 원인을 오류 수집으로 보내고 진단(why)에 남긴다
  const fallbackNote = takeFallbackNote(sessionStore());
  if (fallbackNote) reportTelemetry('error', fallbackReport(fallbackNote));
  const probed = await chooseBackend({
    forceWebGL: compatModeRequested(opts.forceWebGL),
    probeWebGpu: () => probeWebGpu({ gpu: gpuApi, makeCanvas: makeProbeCanvas }),
    probeWebGl2: () => probeWebGl2(makeProbeCanvas),
  });
  const choice: BackendChoice = fallbackNote && probed.backend === 'webgl2' ? { ...probed, why: 'webgpu-runtime-failed:' + fallbackNote.cause } : probed;
  mark('backendChosen');
  if (choice.backend === 'webgl2' && choice.webgpu && !choice.webgpu.ok) console.warn('WebGPU 대신 WebGL2로 시작', choice.why, choice.webgpu.detail ?? '', 'WebGL2 시도 ' + (choice.webgl2?.attempts ?? 1) + '회');
  const renderer = new THREE.WebGPURenderer({ canvas, antialias: false, forceWebGL: choice.backend === 'webgl2', requiredLimits: choice.requiredLimits });
  // 시험을 통과했는데도 WebGPU 초기화가 실패하면 three가 같은 캔버스로 WebGL2를 쓴다. 그 전에 WebGL2가 실제로 되는지 본다.
  guardWebGlFallback(renderer, () => probeWebGl2(makeProbeCanvas));
  // GPU 장치 유실: 상한(2회) 안에서 호환 모드로 다시 열고, 넘으면 안내 화면. 저장소가 막혀도 URL 횟수로 루프를 막는다.
  let disposing = false;
  let lossHandled = false;
  const onGpuLoss = (api: string, reason: string, message: string): void => {
    if (disposing || lossHandled) return;
    lossHandled = true;
    console.error('GPU 장치 유실', api, reason, message);
    const plan = planGpuRecovery(location.href, sessionStore, RENDERER_SESSION_KEY);
    if (plan.reload) { location.replace(plan.url); return; }
    const err = new StartupError('gpu-lost', [api + ' 장치 유실(' + reason + '): ' + message, '자동 재시작 ' + plan.attempts + '회 뒤 중단']);
    if (opts.onFatal) opts.onFatal(err);
    else console.error(err);
  };
  renderer.onDeviceLost = (info) => onGpuLoss(String(info?.api ?? '?'), String(info?.reason ?? 'unknown'), String(info?.message ?? ''));
  /** WebGPU 실행 중 실패(파이프라인 생성 실패·첫 프레임 없음): 원인을 세션에 남기고 장치 유실과 같은 길로 WebGL2로 다시 연다 */
  const runtimeFallback = (cause: FallbackCause, detail: string): void => {
    if (disposing || lossHandled) return;
    saveFallbackNote(sessionStore(), { cause, detail, at: Date.now() });
    onGpuLoss('WebGPU', cause, detail);
  };
  try {
    await renderer.init();
  } catch (e) {
    if (e instanceof StartupError) throw e;
    throw new StartupError('no-gpu', ['렌더러 초기화 실패: ' + String(e)], e);
  }
  // C8-7 고정 프레임 픽스처 동안 셰이더 시계(용암 바다·장판 흐름)를 세계 시계로 고정한다(정상 플레이는 그대로)
  let shaderPin: number | null = null;
  const nodeFrame = (renderer as unknown as { _nodes?: { nodeFrame?: NodeFrameLike } })._nodes?.nodeFrame;
  if (nodeFrame) pinShaderClock(nodeFrame, () => shaderPin);
  // three는 WebGPU 디바이스가 'destroyed' 사유로 끝나면 onDeviceLost를 부르지 않는다(실측: 화면 출력 실패가 이 사유로 끝나 로딩에서 멈춤).
  // 우리가 dispose하지 않았는데 끝났으면 유실로 처리한다.
  const gpuDevice = (renderer.backend as unknown as { device?: { lost?: Promise<{ reason?: string; message?: string }> } }).device;
  gpuDevice?.lost?.then((info) => { if (info?.reason === 'destroyed') onGpuLoss('WebGPU', 'destroyed', String(info?.message ?? '')); });
  const backendAny = renderer.backend as unknown as { isWebGPUBackend?: boolean };
  const backend: BackendName = backendAny.isWebGPUBackend ? 'webgpu' : 'webgl2';
  // WebGPU일 때만: 파이프라인 생성 실패를 three 콘솔에서 잡는다. 시작 화면 전(assetStatus.ready·failed 둘 다 아직)이면 WebGL2로 다시 열고,
  // 플레이 중 늦은 컴파일 실패는 판을 끊지 않도록 오류 수집에만 남긴다(그 물체만 안 보인다).
  // 렌더 루프 감시(첫 render 호출이 FIRST_FRAME_TIMEOUT_MS 안에 없으면 WebGL2)는 createRenderer가 돌아가기 직전에 켠다(아래).
  let unwatchPipelines = (): void => {};
  let firstFrameTimer: ReturnType<typeof setInterval> | null = null;
  let framesDrawn = 0;
  let latePipelineReported = false;
  if (backend === 'webgpu') {
    unwatchPipelines = watchPipelineFailures({ get: () => THREE.getConsoleFunction() as ConsoleFn | null, set: (fn) => THREE.setConsoleFunction(fn as ConsoleFn) }, (detail) => {
      if (!assetStatus.ready && !assetStatus.failed) { runtimeFallback('pipeline', detail); return; } // failed = 캡슐로 플레이 가능(인트로가 연다)
      if (latePipelineReported) return;
      latePipelineReported = true;
      reportTelemetry('error', '[gpu-pipeline-late] ' + detail);
    });
  }
  // 품질 결정: 고정 프리셋(?quality·설정) 또는 자동(기억된 단계 → GPU 정보·화면 픽셀 수로 고른 첫 단계)
  const gpu: GpuInfo = readGpuInfo(renderer, backend);
  const plan: QualityPlan = planQuality(opts.quality, gpu, canvas.clientWidth || viewW(), canvas.clientHeight || viewH(), window.devicePixelRatio || 1);
  const quality: QualityLevel = plan.level;
  const q: QualityStep = plan.spec;
  /** 그림자 파이프라인은 부팅 때만 정한다(켜고 끄면 모든 재질이 다시 컴파일된다). 실행 중 '끄기'는 그림자 패스를 비운다. */
  const shadowCapable = q.shadowMap > 0;
  const governor = plan.mode === 'auto' ? new QualityGovernor(plan.step, backend) : null;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, q.pixelRatio));
  renderer.shadowMap.enabled = shadowCapable;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.info.autoReset = false;

  // 화면 고정 오버레이 층(데미지 숫자·컷인): 캔버스 바로 위, UI 아래.
  const fxLayer = document.createElement('div');
  fxLayer.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;';
  canvas.insertAdjacentElement('afterend', fxLayer);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(MOOD.village.bg);
  scene.fog = new THREE.Fog(MOOD.village.fog, MOOD.village.fogNear, MOOD.village.fogFar);
  const hemi = new THREE.HemisphereLight(MOOD.village.hemiSky, MOOD.village.hemiGround, MOOD.village.hemi);
  const sun = new THREE.DirectionalLight(MOOD.village.sun, MOOD.village.sunI);
  sun.castShadow = shadowCapable;
  if (shadowCapable) {
    sun.shadow.mapSize.set(q.shadowMap, q.shadowMap);
    const sc = sun.shadow.camera;
    sc.left = -26; sc.right = 26; sc.top = 26; sc.bottom = -26; sc.near = 1; sc.far = 90;
    sun.shadow.bias = -0.0004;
  }
  scene.add(hemi, sun, sun.target);

  const clocks = new GameClocks();
  const rig = new CameraRig();
  // C8-6: 터치 화면 보스전은 장면을 화면 폭 5%만큼 왼쪽으로(오른쪽 스킬 단추 호 피하기)
  if (typeof location !== 'undefined' && isTouchDevice(new URLSearchParams(location.search))) rig.touchShift = TOUCH_BOSS_SHIFT;
  const hero = new HeroVisual();
  const avatarLook = new AvatarLook();
  const mobs = new MobsVisual();
  mobs.setDetail(q.mobLodNear, q.mobHdCap, q.mobOutline);
  const boss = new BossVisual();
  boss.lowQuality = quality === 'low';
  // 몹·보스 GLB는 부팅과 함께 읽는다(실패해도 그레이박스로 대신하며 reject하지 않는다)
  // 단계 부팅에서는 마을이 준비된 뒤에 읽는다(warmRest)
  const actorsReady: Promise<unknown> = staged ? Promise.resolve() : Promise.all([mobs.load(), boss.load()]);
  const telegraphs = new TelegraphVisual();
  // 접촉 그림자는 모든 품질에서 유지한다. 그림자 맵은 방향성 그림자만 맡는다
  const blobs = new BlobShadows();
  blobs.setShadowMapEnabled(shadowCapable);
  const slashes = new SlashFx();
  const particles = new ParticleFx(q.particleScale);
  const flash = new FlashLimiter();
  // c021 지역3·4 몹 오라(서리 안개·포자)·처치 폭발(얼음 파편·포자 구름). 섬광 없음
  const regionFx = new RegionMobFx(q.particleScale);
  // 스페셜 몬스터(황금 고블린·챔피언) 오라·반짝이·이름표: 드로우콜 +2(오라 1, 반짝이 1)
  const specialFx = new SpecialFx(q.particleScale, fxLayer);
  // S5 보스 분노·광폭 오라(가산, 드로우콜 +2)
  const bossRageFx = new BossRageFx(q.particleScale);
  const damage = new DamageNumbers(fxLayer, opts.reserve ?? null);
  const eruptions = new EruptionFx();
  scene.add(eruptions.mesh);
  const combatFx = new CombatFx(slashes, particles, flash);
  // 품질 단계별 전투 이펙트 배율(불똥·파편 수, 스프라이트 상한)
  combatFx.setScale(q.particleScale);
  void combatFx.load();
  scene.add(hero.root, avatarLook.aura, mobs.group, boss.root, telegraphs.group, slashes.group, particles.group, combatFx.group, blobs.mesh, regionFx.group, specialFx.group, bossRageFx.group);
  // VFX 모듈 라이브러리(D1d): Ev.SkillCue vfx 큐를 모듈 조합(cueMap.ts)으로 그린다. c012: Kael 스킬 5종 전부 live
  const skillModules = new VfxModules();
  scene.add(skillModules.group);
  combatFx.skillsByModules = true;
  // c022 라이라: 화살 렌더·라이라 큐 조합(모듈 라이브러리를 같이 써서 화살 2 드로우콜만 늘어난다)·그물 덫 속박 고리(1 드로우콜)
  const lyraFx = new LyraFx(skillModules);
  const lyraLink = new LyraLink(lyraFx);
  const rootRings = new RootRings(view.mobs.capacity);
  scene.add(lyraFx.group, rootRings.mesh);
  // 레이드(RAID-PRD): AI 용병 3명(카엘 리그 복제)과 불 장판·대상 표식. 용병 모델은 레이드에 처음 들어갈 때 읽고 그때 컴파일한다
  const mercs = new MercsVisual();
  const raidFx = new RaidFx();
  const hellFx = new HellFx(q.particleScale);
  hellFx.onPunch = (px, ms, dx, dz) => rig.punch(px, ms, dx, dz);
  scene.add(mercs.group, raidFx.group, hellFx.group);
  // 레이드 VFX(브레스·화염구·착지 충격파·이륙 바람·물기·꼬리·파티 직업 AI·격노): 드로우콜 6, 카메라 펀치는 기존 CameraRig
  const raidVfx = new RaidVfx(q.particleScale);
  raidVfx.onPunch = (px, ms, dx, dz) => rig.punch(px, ms, dx, dz);
  scene.add(raidVfx.group);
  let raidWarm = false;
  // 근접 카메라에서 보스가 화면 밖으로 나가면 가장자리 화살표(줌을 당겼을 때만 켜진다)
  const bossPointer = new BossPointer(document.getElementById('ui'));
  /** 라이라 외형(카엘 리그 위 활·화살통·청록 색조). 붙인 몸 메시(단계 교체로 바뀌면 다시 붙인다)와 실패 기록 */
  let lyraLook: LyraLook | null = null;
  let lyraLookMesh: unknown = null;
  let lyraLookFailed: unknown = null;
  /** 라이라 장궁 GLB: 바라는 단계·받은 지오메트리와 그 단계·지금 외형이 쓰는 단계(0 = 절차 활) */
  let lyraBowWant = 0, lyraBowTier = 0, lyraLookBow = 0;
  let lyraBowGeo: THREE.BufferGeometry | null = null;
  /** 카엘 몸과 라이라 전용 몸(LYRA_URL, 라이라를 처음 고를 때 한 번 읽는다). 읽기 실패면 카엘 몸 + 색조 외형으로 남는다 */
  let kaelBody: KaelModel | null = null;
  let lyraBody: KaelModel | null = null;
  let lyraBodyState: 'none' | 'loading' | 'ready' | 'failed' = 'none';
  /** 라이라 스킬 끊김 감지(화살비 지연 낙하 지우기): 직전 프레임 동작·스킬 */
  let lastAct = -1, lastSkill = 0, lastActTick = 0;
  let heroShown: 'kael' | 'lyra' = 'kael';
  // 칼날 폭풍이 끊기면 흡입 소용돌이를 그 자리에서 지운다(COMBAT-FEEL 9.2)
  combatFx.onSkillCut = (id) => { skillModules.cutSkill(id); };
  // 화면 펄스(낙화·런처 비네트)도 각성·처형 섬광과 같은 섬광 예산(1초 3회·간격 334ms)을 쓴다(실시간 기준, Codex P1 c013)
  // 스킬 화면 펄스는 옅은 펄스: 각성·처형 우선 섬광이 대기·예약 중이면 양보한다(c015)
  skillModules.screen.setGate({ admit: () => flash.admitSoft(clocks.realMs) });
  // 피격 상태 연출(c012): 다운 흙먼지·균열, 기상 무적 오라, 다운 추격 낮은 불똥. 모듈을 같이 써서 드로우콜이 늘지 않는다
  const hitStates = new HitStateFx(skillModules, particles);
  hitStates.setScale(q.particleScale);
  // 장비 연출(c017): 드롭 줍기(접히는 기둥·나선 줄기·도착 고리)와 전설 고유 효과(강철 거품·처치 알갱이·가장자리 맥동). 모듈 라이브러리를 같이 쓴다
  const gearFx = new GearFx(skillModules, particles);
  gearFx.setScale(q.particleScale);
  // 자동 퀘스트 길 안내(바닥 금색 점선·목적지 표식)
  const autoRouteFx = new AutoRouteFx();
  scene.add(autoRouteFx.group);
  // 환생(레인 R): 마을① 윤회의 문 + 환생 영웅 발밑 오라
  const rebirthFx = new RebirthFx();
  scene.add(rebirthFx.group);
  // 바닥 드롭 빛기둥(D2a): 등급 색 기둥 + 보석, 강화석 결정. 인스턴싱 5개(기둥·보석·강화석·고리·불꽃)
  const lootFx = new LootFx(view.loot.capacity);
  scene.add(lootFx.group);
  // 바닥 드롭 이름표(드롭 식별): 가까운 드롭 위에 그림 + 이름 + 종류 태그. DOM 풀 8개, 겹침 정리
  const lootLabels = new LootLabels(fxLayer);
  const post = new PostFx(renderer, scene, rig.camera, q);
  /**
   * 미리 컴파일·굽기는 실제 장면 그리기와 같은 렌더 컨텍스트로 해야 파이프라인이 재사용된다.
   * 실제 장면은 후처리(RenderPipeline 화면 사각형 그리기, 호출 깊이 0) 안에서 scenePass가 다시 render를 불러 **호출 깊이 1**,
   * 렌더 타깃 scenePass(MSAA 4·rgba16float·depth24plus-stencil8)로 그려진다. three r186은 렌더 컨텍스트를 (타깃·MRT·호출 깊이)로 나누고
   * 그 id가 RenderObject 캐시 키에 들어가므로, 캔버스·깊이 0으로 한 compileAsync는 노드·셰이더를 처음부터 다시 만들게 했다
   * (2026-11-03 PC 측정: 던전 입장 직후 동기 createRenderPipeline 44~54개, GPU 1.1~1.6초 멈춤. 짝 비교에서 정점 셰이더 버퍼 노드만 달랐다).
   * 그래서 compileAsync는 scenePass 타깃 + 깊이 1 컨텍스트를, 굽기 render는 깊이 0에서 시작해 안쪽이 1이 되게 한다(그림자 패스는 그 안쪽 2로 실제와 같다).
   */
  type RenderContextsLike = { get(target: unknown, mrt?: unknown, callDepth?: number): unknown };
  const internals = renderer as unknown as { _renderContexts?: RenderContextsLike; _callDepth?: number };
  const SCENE_PASS_DEPTH = 1;
  const compileAsync = (obj: THREE.Object3D, camera: THREE.Camera, target: THREE.Scene | null = null): Promise<void> => {
    // 가산 투명 양면 재질은 두 번 나눠 그리지 않게(나눠 그리면 BackSide·FrontSide 파이프라인이 첫 그리기에서 동기로 생긴다, singlePass.ts)
    singlePassAdditive(obj);
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(post.sceneTarget);
    const rc = internals._renderContexts;
    const origGet = rc?.get;
    // compileAsync는 시작하자마자(동기 구간) 컨텍스트를 한 번 정한다: 그 한 번만 깊이를 scenePass와 같게 준다
    if (rc && origGet) rc.get = (t: unknown, m?: unknown, d?: number) => origGet.call(rc, t, m, d ?? SCENE_PASS_DEPTH);
    try {
      return renderer.compileAsync(obj, camera, target);
    } finally {
      if (rc && origGet) rc.get = origGet;
      renderer.setRenderTarget(prev);
    }
  };
  /**
   * 장면을 scenePass와 같은 컨텍스트(타깃·호출 깊이 1)로 한 번 그린다. 결과는 다음 프레임이 덮어쓴다.
   * 굽기 카메라는 굽기마다 새로 만든다: three r186 ShadowNode.updateBefore는 (카메라, frameId)마다 그림자를 한 번만 그리는데,
   * 굽기는 같은 rAF 안에서 실제 프레임(rig.camera)이 이미 그림자를 그린 뒤에 돌아 그림자 패스가 건너뛰어졌다
   * (after6 데스크톱: 입장 순간 스테이지 환경 그림자 파이프라인 7개 동기 생성). RenderObject 키는 (객체·재질·컨텍스트·조명)이라
   * 카메라가 달라도 실제 프레임과 같은 RenderObject·파이프라인을 쓴다.
   */
  const renderAsScenePass = (): void => {
    const cam = rig.camera.clone();
    const prev = renderer.getRenderTarget();
    const depth = internals._callDepth;
    renderer.setRenderTarget(post.sceneTarget);
    if (typeof depth === 'number') internals._callDepth = SCENE_PASS_DEPTH - 1;
    try {
      renderer.render(scene, cam);
    } finally {
      if (typeof depth === 'number') internals._callDepth = depth;
      renderer.setRenderTarget(prev);
    }
  };
  const awaken = new AwakenFx(fxLayer, post, slashes, flash, view, quality);
  // 결투장식 KO 화면(어둡게·붉은 테두리·집중선)과 푸시인 대기(참격 순간 켜고, 그 동작의 첫 타격이 끈다)
  const koScreen = new KoScreen(post);
  let pushArmed = false;

  // 맵 캐시: 두 맵의 그레이박스를 부팅 때 한 번 만들고 보이기만 바꾼다(재생성·재질 누수 없음).
  // 환경 아트는 첫 맵(실제 게임은 마을)만 시작 버튼 전에 읽고, 나머지 맵은 시작 버튼이 켜진 뒤 뒤에서 읽는다.
  const mapCache = new Map<MapDef, MapVisual>();
  const ensureMap = (m: MapDef): MapVisual => {
    let mv = mapCache.get(m);
    if (!mv) {
      mv = buildMap(m, m === view.map ? view.grid : rasterize(m), { quality });
      mapCache.set(m, mv);
      scene.add(mv.group);
    }
    return mv;
  };
  ensureMap(MAPS.village);
  ensureMap(MAPS.r1);
  ensureMap(MAPS.r2);
  // D3a: 열린 지역 지도와 도착한 마을(+ 다음 마을) 변형도 미리 만들어 둔다 — 시작 버튼 뒤 prepareRest가 환경을 뒤에서 읽어,
  // 마을 이동 연출·새 지역 첫 입장 때 그레이박스가 보이지 않게
  const pv = view.progress;
  if (pv) {
    if (pv.regionUnlocked('r3')) ensureMap(MAPS.r3);
    if (pv.regionUnlocked('r4')) ensureMap(MAPS.r4);
    if (pv.regionUnlocked('r5')) ensureMap(MAPS.r5);
    if (pv.regionUnlocked('r6')) ensureMap(MAPS.r6);
    for (let t = 2; t <= Math.min(VILLAGES.length, pv.highestTown + 1); t++) ensureMap(VILLAGES[t - 1]);
  }
  // S2: 지역1 스테이지 지도는 지금 도전할 스테이지(frontier)의 판만 미리 만든다. 나머지 판은 들어갈 때 만들고 환경이 읽히는 순간 그레이박스와 바뀐다
  // 최전선·고른(노말·하드) 스테이지의 지도는 환경 예열 대상이기도 하다(warmRest) — 같은 MapDef 객체가 캐시 키
  const warmStageMaps = stageMapsToWarm(pv, view.hard);
  for (const m of warmStageMaps) ensureMap(m);
  // ARPG 필드(레인 F): 구역 지형은 청크 렌더가 따로 그리고(맵 캐시에 넣지 않는다), 표식(마을 필드 문·균열 통로)은 늘 장면에 있다
  const fieldRender = new FieldRender(quality);
  scene.add(fieldRender.group, fieldRender.markers);
  fieldRender.bindFx(scene.fog as THREE.Fog, rig.camera, (id, x, z) => rig.cue(id, x, z)); // 대균열 연출(C12): 층 안개·입자 빌보드·돌파 컷
  // MMO 공유 필드: 다른 플레이어·월드 보스(필드 장면에서 연결됐을 때만 보인다)
  const fieldMmo = new FieldMmoRender();
  scene.add(fieldMmo.group);
  // 파티 판(레인 D): 동료 영웅(같은 PeersVisual, 이름표 포함). 파티 판에서만 보인다
  const partyPeers = new PartyPeersRender();
  scene.add(partyPeers.group);
  // 레이드 4인 넷 판: 다른 사람 영웅(같은 PeersVisual). 넷 판에서만 보인다
  const raidPeers = new RaidPeersRender();
  scene.add(raidPeers.group);
  const firstMap = ensureMap(fieldRender.owns(view.map) ? MAPS.village : view.map);
  firstMap.load();
  let activeMap: MapVisual | null = null;
  let builtFor: MapDef | null = null;
  let lastTowerTheme = '';

  const applyMood = (map: MapDef): void => {
    const id = map.id;
    const m = towerMoodOf(view.flow.stageId, moodOf(map)) ?? moodOf(map);
    (scene.background as THREE.Color).setHex(m.bg);
    const fog = scene.fog as THREE.Fog;
    fog.color.setHex(m.fog);
    fog.near = m.fogNear;
    fog.far = m.fogFar;
    hemi.color.setHex(m.hemiSky);
    hemi.groundColor.setHex(m.hemiGround);
    hemi.intensity = m.hemi;
    sun.color.setHex(m.sun);
    sun.intensity = m.sunI;
    // 잿불에서도 흰 머리와 갑옷 윤곽은 차가운 하늘빛으로 분리한다. 다른 맵은 원래 진홍 림.
    hero.rim.color.value.setHex(view.heroId === 'lyra' ? 0x3fd0c0 : id === 'r2' ? 0xc3d9f2 : PALETTE.crimson);
    post.setMood(m);
    renderer.toneMappingExposure = m.exposure;
  };

  let width = 1, height = 1;
  const resize = (): void => {
    width = canvas.clientWidth || viewW();
    height = canvas.clientHeight || viewH();
    renderer.setSize(width, height, false);
    rig.resize(width, height);
  };
  resize();

  // ── 품질 단계 적용(부팅·자동 조절) ─────────────────────
  let cur: QualityStep = q;
  let shadowOn = shadowCapable;
  let kaelOutline = q.kaelOutline;
  let decorOn = q.decor;
  const decorCache = new Map<MapVisual, THREE.Object3D[]>();
  /**
   * 환경 장식 층: 환경 루트에서 마지막 키트 메시 뒤에 붙는 효과(풀·꽃·불꽃·빛·웅덩이·빛줄기)와 작은 소품 키트.
   * 환경은 실행 중 다시 배치하지 않으므로(env/** 계약) 밀도 대신 이 층의 보이기만 바꾼다.
   */
  const decorOf = (mv: MapVisual): THREE.Object3D[] | null => {
    if (mv.envState !== 'ready') return null;
    let list = decorCache.get(mv);
    if (list) return list;
    // 지역 환경('env-…')과 스테이지 환경('stage-env-…') 루트를 모두 찾는다(env/decor.ts)
    const env = findEnvRoot(mv.group.children);
    list = env ? decorChildren(env) : [];
    decorCache.set(mv, list);
    return list;
  };
  const applyDecor = (): void => {
    for (const mv of mapCache.values()) {
      const list = decorOf(mv);
      if (list) for (const o of list) o.visible = decorOn;
    }
  };
  /** 그림자 끄기: 그림자 카메라를 아무 개체도 없는 층(31)만 보게 해 한 번 비우고 갱신을 멈춘다(재질 재컴파일 없음). */
  const setShadow = (size: number): void => {
    if (!shadowCapable) { blobs.setShadowMapEnabled(false); return; }
    const on = size > 0;
    if (on) {
      sun.shadow.mapSize.set(size, size);
      sun.shadow.camera.layers.mask = 1;
      sun.shadow.autoUpdate = true;
    } else if (shadowOn) {
      sun.shadow.camera.layers.set(31);
      sun.shadow.autoUpdate = false;
      sun.shadow.needsUpdate = true;
    }
    shadowOn = on;
    blobs.setShadowMapEnabled(on);
  };
  const applyStep = (s: QualityStep): void => {
    cur = s;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, s.pixelRatio));
    resize();
    setShadow(s.shadowMap);
    // MSAA는 부팅 값 고정(실행 중 표본 수 변경 = 전체 파이프라인 재생성, 수 초 멈춤)
    post.setQuality(q.msaa, s.bloomScale);
    mobs.setDetail(s.mobLodNear, s.mobHdCap, s.mobOutline);
    kaelOutline = s.kaelOutline;
    combatFx.setScale(s.particleScale);
    hitStates.setScale(s.particleScale);
    gearFx.setScale(s.particleScale);
    raidVfx.setScale(s.particleScale);
    if (decorOn !== s.decor) { decorOn = s.decor; applyDecor(); }
  };

  // 셰이더 미리 컴파일: 모든 메시·VFX 풀을 보이게 두고 compileAsync → 후처리까지 한 프레임 그린 뒤 원래대로.
  if (staged) {
    // 단계 부팅: 맵 그레이박스는 보이기만 끄고, 컴파일은 마을 환경(prepareEnv)과 Kael이 필요한 것만 먼저 한다
    for (const mv of mapCache.values()) mv.group.visible = false;
  } else {
    await actorsReady;
    mobs.prewarm(true);
    eruptions.mesh.count = 1;
    boss.root.visible = true;
    blobs.mesh.visible = true;
    blobs.mesh.count = 1;
    slashes.prewarm(true);
    particles.prewarm(true);
    regionFx.prewarm(true);
    specialFx.prewarm(true);
    bossRageFx.prewarm(true);
    combatFx.prewarm(true);
    skillModules.prewarm(true);
    lyraFx.arrows.prewarm(true);
    rootRings.prewarm(true);
    hellFx.prewarm(true); // 헬 각인 장판·룬·폭발 셰이더(첫 폭발 끊김 방지)
    for (const mv of mapCache.values()) mv.group.visible = true;
    rig.update(0, 0, 0);
    try {
      await compileAsync(scene, rig.camera);
    } catch (err) {
      console.warn('미리 컴파일 실패(첫 프레임에 컴파일된다)', err);
    }
    post.render();
    awaken.prewarm();
    boss.root.visible = false;
    blobs.mesh.count = 0;
    blobs.mesh.visible = true;
    mobs.prewarm(false);
    eruptions.mesh.count = 0;
    slashes.prewarm(false);
    particles.prewarm(false);
    regionFx.prewarm(false);
    specialFx.prewarm(false);
    bossRageFx.prewarm(false);
    combatFx.prewarm(false);
    skillModules.prewarm(false);
    lyraFx.arrows.prewarm(false);
    rootRings.prewarm(false);
    hellFx.prewarm(false);
    for (const mv of mapCache.values()) mv.group.visible = false;
  }

  mark('boot');
  // 환경 아트: 읽기 → 숨긴 채 미리 컴파일 → 그레이박스와 교체. 맵마다 한 번만 돈다.
  const envJobs = new Map<MapVisual, Promise<void>>();
  const prepareEnv = (mv: MapVisual, name: string): Promise<void> => {
    let job = envJobs.get(mv);
    if (!job) {
      job = mv.load().then(async () => {
        mark(name + 'Loaded');
        mv.compileView(true);
        const p = compileAsync(mv.group, rig.camera, scene);
        mv.compileView(false);
        await p.catch((err: unknown) => console.warn('환경 미리 컴파일 실패(첫 프레임에 컴파일된다)', err));
        mv.reveal();
        // 교체 직후 몇 프레임은 업로드·컴파일로 느릴 수 있다: 조절기가 이것을 기기 성능으로 읽지 않게 한다
        governor?.hold(performance.now(), 1500);
        mark(name + 'Ready');
        if (mv === firstMap) { stage.env = true; pushProgress(); }
      });
      envJobs.set(mv, job);
    }
    return job;
  };
  /** 환경 준비가 끝난(성공·실패 모두) 지도: 스테이지 입장 대기 화면이 기다린다 */
  const envSettled = new Set<MapVisual>();
  const prepareEnvTracked = (mv: MapVisual, name: string): Promise<void> => {
    const job = prepareEnv(mv, name);
    if (!envSettled.has(mv)) void job.then(() => envSettled.add(mv), () => envSettled.add(mv));
    return job;
  };
  const envReady = prepareEnv(firstMap, view.map.id);
  /** 첫 맵 밖의 맵(던전)은 시작 버튼이 켜진 뒤 뒤에서 준비한다. 들어갈 때까지 못 끝나면 그레이박스로 시작해 끝나는 순간 바뀐다. */
  const prepareRest = (): void => {
    for (const [m, mv] of mapCache) if (mv !== firstMap) void prepareEnv(mv, m.id);
  };

  /** 단계 부팅에서 던전별 자원 준비 완료 표시(r1: 공통 몹·보스·VFX + R1 환경, r2: 여기에 R2 전용 몹·보스 + R2 환경) */
  const warm = { r1: !staged, r2: !staged };
  /** 대상 하나를 잠깐 보이게 해 컴파일 목록에 담은 뒤 곧바로 되돌린다(목록은 호출 시점에 확정되고, three가 개체 사이마다 메인 스레드를 돌려준다). */
  /**
   * 남은 숨은 풀 미리 컴파일: 모듈별 prewarm 목록에 없는 풀(텔레그래프·피 자국·접지 그림자·이름 없는 VFX 묶음 등)은
   * 마을에서는 숨겨져 있어 compileAsync가 건너뛰고, 던전에 들어가 처음 보일 때 동기로 만들어졌다
   * (2026-11-03 after6 데스크톱·모바일: 입장 순간 이름 없는 인스턴스 풀 11개, 클라우드 식별: aTele·bloodOpacity·contactOpacity·aAlpha 형상 등).
   * 지도·몹·보스·주인공처럼 따로 예열하는 묶음은 빼고, 나머지 장면 자식 트리를 잠깐 전부 보이게(빈 인스턴스는 1개로) 한 뒤 컴파일하고 정확히 되돌린다.
   */
  const compileHiddenPools = async (): Promise<void> => {
    const skip = new Set<THREE.Object3D>([boss.root, mobs.group]);
    for (const mv of mapCache.values()) skip.add(mv.group);
    const roots = scene.children.filter((o) => !skip.has(o) && !(o as THREE.Light).isLight);
    const vis: THREE.Object3D[] = [];
    const cnt: Array<[THREE.InstancedMesh, number]> = [];
    const visit = (o: THREE.Object3D): void => {
      if (skip.has(o)) return;
      if (!o.visible) { o.visible = true; vis.push(o); }
      const im = o as THREE.InstancedMesh;
      if (im.isInstancedMesh && im.count === 0) { cnt.push([im, im.count]); im.count = 1; }
      for (const c of o.children) visit(c);
    };
    for (const r of roots) visit(r);
    const ps = roots.map((r) => compileAsync(r, rig.camera, scene));
    for (const o of vis) o.visible = false;
    for (const [im, c] of cnt) im.count = c;
    await Promise.all(ps.map((p) => p.catch((err: unknown) => console.warn('숨은 풀 미리 컴파일 실패(처음 쓸 때 컴파일된다)', err))));
  };
  const compileGroup = async (on: () => void, group: THREE.Object3D, off: () => void): Promise<void> => {
    on();
    const p = compileAsync(group, rig.camera, scene);
    off();
    await p.catch((err: unknown) => console.warn('미리 컴파일 실패(처음 쓸 때 컴파일된다)', err));
  };
  /**
   * 지도 굽기: 환경이 준비된(reveal 뒤) 지도를 컬링 없이 보이게 한 채 실제 장면 렌더 타깃에 한 번 그린다(그림자 패스 포함).
   * compileAsync는 그림자 패스 파이프라인을 만들지 않고, 스테이지 환경 일부는 실제 그리기에서 셰이더가 다시 만들어져
   * 던전 입장 직후 동기 파이프라인 44~54개가 GPU를 1.1~1.6초 막았다(2026-11-03 PC 측정, perf-first30 --gpu-calls).
   * 그 생성을 단계 부팅(로딩 막대·타이틀 뒤)으로 당긴다. 그린 결과는 다음 프레임이 덮어쓰므로 화면에 보이지 않는다.
   */
  const bakeMap = (mv: MapVisual): void => {
    if (mv === firstMap || disposing) return;
    mv.compileView(true);
    try {
      renderAsScenePass();
    } catch (err) {
      console.warn('지도 굽기 실패(처음 그릴 때 만들어진다)', err);
    } finally {
      mv.compileView(false);
    }
    governor?.hold(performance.now(), 1500);
  };
  const warmMobs = (): Promise<void> => compileGroup(() => mobs.prewarm(true), mobs.group, () => mobs.prewarm(false));
  const warmBoss = (): Promise<void> => compileGroup(() => { boss.root.visible = true; }, boss.root, () => { boss.root.visible = false; });
  /** 뒤쪽 준비 진행(0..1): 인트로 로딩 화면이 마지막 구간 막대에 쓴다 */
  let warmFrac = staged ? 0 : 1;
  /**
   * 마을이 준비된 뒤 R1 던전 자원(몹·보스·VFX·환경)을 덩이씩 읽고 미리 컴파일한다.
   * 측정(work/first-load/RESULT.md): 이 덩이들은 몇 초씩 메인 스레드를 잡는 구간이 있어 마을에서 돌면 끊긴다.
   * 그래서 인트로가 이 구간이 끝나길(최대 몇 초) 기다리고, 가장 무거운 R2 자원은 R2에 처음 들어갈 때 대기 화면 뒤에서 준비한다.
   */
  const warmRest = async (): Promise<void> => {
    try {
      await Promise.all([mobs.loadCore(), boss.loadCore()]);
      mark('actorsLoaded'); warmFrac = 0.25;
      await warmMobs();
      await warmBoss(); warmFrac = 0.4;
      await compileGroup(() => { eruptions.mesh.count = 1; }, eruptions.mesh, () => { eruptions.mesh.count = 0; });
      await compileGroup(() => { blobs.mesh.visible = true; blobs.mesh.count = 1; }, blobs.mesh, () => { blobs.mesh.count = 0; });
      await compileGroup(() => slashes.prewarm(true), slashes.group, () => slashes.prewarm(false));
      await compileGroup(() => particles.prewarm(true), particles.group, () => particles.prewarm(false));
      await compileGroup(() => regionFx.prewarm(true), regionFx.group, () => regionFx.prewarm(false));
      await compileGroup(() => specialFx.prewarm(true), specialFx.group, () => specialFx.prewarm(false));
      await compileGroup(() => bossRageFx.prewarm(true), bossRageFx.group, () => bossRageFx.prewarm(false));
      await compileGroup(() => combatFx.prewarm(true), combatFx.group, () => combatFx.prewarm(false));
      await compileGroup(() => skillModules.prewarm(true), skillModules.group, () => skillModules.prewarm(false));
      await compileGroup(() => lyraFx.arrows.prewarm(true), lyraFx.group, () => lyraFx.arrows.prewarm(false));
      await compileGroup(() => rootRings.prewarm(true), rootRings.mesh, () => rootRings.prewarm(false));
      await compileHiddenPools();
      awaken.prewarm();
      mark('actorsWarm'); warmFrac = 0.6;
      const r1v = mapCache.get(MAPS.r1);
      if (r1v && r1v !== firstMap) { await prepareEnvTracked(r1v, 'r1'); bakeMap(r1v); }
      // 최전선·고른 스테이지의 지역1 변형 지도(A′·M·B·B′·F)도 여기서 환경을 준비한다 — MAPS.r1(A)만 예열하면 첫 입장 때 렌더 루프에서야 시작된다
      for (const m of warmStageMaps) { const sv = mapCache.get(m); if (sv && sv !== firstMap && sv !== r1v) { await prepareEnvTracked(sv, m.stageKey ? m.id + m.stageKey : m.id); bakeMap(sv); } }
      mark('warmR1');
    } catch (err) {
      console.warn('던전 자원 준비 실패(처음 쓸 때 읽거나 컴파일된다)', err);
    } finally {
      warm.r1 = true;
      warmFrac = 1;
    }
  };
  let r2Started = false;
  let r1WarmP: Promise<void> = Promise.resolve();
  /** R2 전용 몹·보스·환경. 처음 R2에 들어갈 때 대기 화면(waiting) 뒤에서 읽고 컴파일한다. */
  const warmR2 = async (): Promise<void> => {
    if (r2Started) return;
    r2Started = true;
    try {
      await r1WarmP;
      await Promise.all([mobs.loadR2(), boss.loadEmber()]); mark('r2Actors');
      await warmMobs();
      await warmBoss();
      const r2v = mapCache.get(MAPS.r2);
      if (r2v && r2v !== firstMap) { await prepareEnv(r2v, 'r2'); bakeMap(r2v); }
    } catch (err) {
      console.warn('R2 자원 준비 실패(처음 쓸 때 읽거나 컴파일된다)', err);
    } finally {
      warm.r2 = true;
      mark('warmR2');
    }
  };

  /**
   * c021 지역 변형(r3 서리·r4 독) 몹·보스: 처음 그 지역에 들어갈 때 R2 준비와 함께 대기 화면 뒤에서 조각을 읽어 붙이고 새 몹 재질을 컴파일한다.
   * 보스 변형 메시는 원형 재질·속성 배치를 그대로 쓰므로 다시 컴파일하지 않는다. 실패해도 색조판으로 계속 플레이된다.
   */
  const LOOK_WARM_TIMEOUT_MS = 8000;
  const lookWarm: Record<RegionLook, boolean> = { frost: false, poison: false, sand: false, void: false };
  const lookStarted: Record<RegionLook, boolean> = { frost: false, poison: false, sand: false, void: false };
  const warmLook = async (look: RegionLook): Promise<void> => {
    if (lookStarted[look]) return;
    lookStarted[look] = true;
    try {
      await r1WarmP;
      await actorsReady;
      // 조각 읽기·재질 컴파일이 LOOK_WARM_TIMEOUT_MS를 넘기면 대기 화면을 붙잡지 않고 색조판으로 먼저 진행한다(작업은 뒤에서 계속)
      const work = (async () => {
        await Promise.all([mobs.loadLook(look), boss.loadLook(look)]); mark(look + 'Actors');
        await warmMobs();
      })();
      work.catch((err) => console.warn('지역 변형 준비 실패(처음 쓸 때 읽거나 컴파일된다): ' + look, err));
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<'timeout'>((res) => { timer = setTimeout(() => res('timeout'), LOOK_WARM_TIMEOUT_MS); });
      const r = await Promise.race([work.then(() => 'ok' as const), timeout]);
      clearTimeout(timer);
      if (r === 'timeout') console.warn('지역 변형 준비가 ' + LOOK_WARM_TIMEOUT_MS / 1000 + '초를 넘겨 색조판으로 먼저 진행한다: ' + look);
    } catch (err) {
      console.warn('지역 변형 준비 실패(처음 쓸 때 읽거나 컴파일된다): ' + look, err);
    } finally {
      lookWarm[look] = true;
      mark(look + 'Warm');
    }
  };

  const resetScene = (): void => {
    rig.snap();
    hero.snap();
    damage.clear();
    slashes.clear();
    particles.clear();
    regionFx.clear();
    specialFx.clear();
    lootLabels.clear();
    bossRageFx.clear();
    hellFx.clear();
    combatFx.clear();
    skillModules.clear();
    lyraLink.clear();
    rootRings.clear();
    hitStates.clear();
    gearFx.clear();
    eruptions.pool.reset();
    mobs.reset();
    awaken.reset();
    koScreen.reset();
    pushArmed = false;
    governor?.hold(performance.now(), 1500);
  };

  // QA 디버그 훅(?qa 또는 개발 서버에서만): 조건이 맞는 프레임 직후 그리기를 잠깐 멈추고(마지막 화면 유지) 캔버스를 캡처한다.
  interface DebugInfo { flash: number; granted: number }
  interface DebugHit { tick: number; act: number; step: number; phase: number; actTick: number; awaken: number; flash: number; granted: number; shot: string }
  /** 감시 목록: 조건이 처음 맞는 프레임의 캔버스(3D 화면, DOM 제외)를 저장한다. 그리기를 멈추지 않는다. */
  interface DebugWatch { name: string; when: (v: GameView, d: DebugInfo) => boolean; hit: DebugHit | null }
  const debugOn = new URLSearchParams(location.search).has('qa') || !!import.meta.env.DEV;
  const dbg = {
    when: null as null | ((v: GameView, d: DebugInfo) => boolean),
    ms: 800,
    capture: false,
    frozenUntil: 0,
    hits: [] as DebugHit[],
    watches: [] as DebugWatch[],
    status: assetStatus,
    timings: () => hero.model?.table ?? null,
    attacks: () => hero.model?.attacks.map((a) => ({ clip: a.clip, hit: a.hit, s0: a.s0, s1: a.s1, ratePre: a.ratePre, ratePost: a.ratePost, headStart: a.headStart, drawTicks: a.drawTicks })) ?? null,
    zoom: (f: number, lookY = 0) => rig.setDebugZoom(f, lookY),
    pose: (pitchDeg: number, distance: number) => rig.setDebugPose(pitchDeg, distance),
    /** 전투 연출 컷을 직접 건다(QA). 진행 중 컷 이름과 카메라 상태 */
    cine: (id: ShotId, x = 0, z = 0) => rig.cue(id, x, z),
    cineActive: () => rig.cine.active,
    camera: () => { const c = rig.camera; return { x: c.position.x, y: c.position.y, z: c.position.z, fov: c.fov, cine: rig.cine.active, pose: { ...rig.cine.pose } }; },
    /** QA: 이번 프레임 타격 반동만(m·px). 흔들림 끔이면 0, 약하게는 보통의 0.5배, 섬광 약화는 0.35배 */
    punch: () => ({ m: rig.punchQa.m, px: rig.punchQa.px }),
    /** QA: 레이드 VFX 살아 있는 입자·판·누적 연출 수 */
    raidVfx: () => raidVfx.stats,
    sword: (px: number, py: number, pz: number, rx: number, ry: number, rz: number) => hero.model?.setSwordMount(px, py, pz, rx, ry, rz, true),
    faceFlip: () => hero.model?.faceFlip ?? null,
    /** QA 진단 전용: 카엘 모델(단계 교체·외곽선 확인) */
    kaelModel: () => hero.model,
    /** 로딩 구간(ms, createRenderer 시작 기준)과 맵별 환경 상태 */
    loadTimes: () => loadTimes,
    envStatus: () => {
      const s: Record<string, string> = {};
      for (const [m, mv] of mapCache) s[m.id] = mv.envState;
      return s;
    },
    /** 품질 계획·GPU 정보·조절기 상태와 변경 기록 */
    gpu: () => gpu,
    quality: () => ({ mode: plan.mode, reason: plan.reason, start: plan.step, shadowCapable, msaa: q.msaa, ladder: QUALITY_LADDER.map((s) => s.label), current: cur.label, governor: governor ? governor.state() : null, history: governor ? governor.history.slice() : [] }),
    /** QA: 자동 조절 단계를 강제로 바꾼다(조절기는 그 단계에서 계속 판단한다). n<0이면 조절기를 멈춘다 */
    setStep: (n: number) => {
      if (!governor) return false;
      if (n < 0) { governor.enabled = false; return true; }
      const s = Math.max(0, Math.min(QUALITY_LADDER.length - 1, n | 0));
      governor.step = s;
      governor.hold(performance.now());
      applyStep(QUALITY_LADDER[s]);
      return true;
    },
    /** QA: 마지막 프레임 x-ray 판정 */
    xray: () => xrayOn,
    /** QA: VFX 모듈 라이브러리(활성 수·드로우콜·큐 집계)와 피격 상태 연출 집계(읽기 전용) */
    modules: () => ({ active: skillModules.activeCount, drawCalls: skillModules.drawCalls, cues: { ...skillModules.cues }, cuts: skillModules.cuts, perModule: skillModules.all.map((m) => ({ kind: m.kind, active: m.activeCount, drawCalls: m.drawCalls })), hitStates: { ...hitStates.stats }, gear: { ...gearFx.stats } }),
    /** QA(c022): 라이라 연결 집계(받은 화살·끝·큐), 화살 렌더(활성·쏜 수), 화살비 큐, 외형·시위, 속박 고리 수 */
    lyra: () => ({ link: { ...lyraLink.stats }, arrows: { active: lyraFx.arrows.activeCount, fired: lyraFx.arrows.fired, landed: lyraFx.arrows.landed, replaced: lyraFx.arrows.replaced }, fx: { ...lyraFx.cues, pending: lyraFx.pendingRain }, look: lyraLook ? { draw: lyraLook.draw, drawCalls: lyraLook.drawCalls } : null, rootRings: rootRings.mesh.count, hero: heroShown }),
    /** QA(c8-5): 비룡 Head 뼈와 머리 근처 최고점(뿔 끝)의 화면 좌표(CSS px). 읽기 전용, 비룡 GLB가 없으면 null */
    bossHeadScreen: () => {
      const root = boss.root;
      const head = root.getObjectByName('Head');
      let skinned: THREE.SkinnedMesh | null = null;
      root.traverse((o) => { if (!skinned && (o as THREE.SkinnedMesh).isSkinnedMesh) skinned = o as THREE.SkinnedMesh; });
      const mesh = skinned as THREE.SkinnedMesh | null;
      if (!head || !mesh) return null;
      root.updateMatrixWorld(true);
      const cam = rig.camera;
      cam.updateMatrixWorld();
      const rc = renderer.domElement.getBoundingClientRect();
      const hp = new THREE.Vector3(); head.getWorldPosition(hp);
      const v = new THREE.Vector3();
      let best: THREE.Vector3 | null = null;
      const R2 = 2.2 * 2.2; // 머리 뼈에서 2.2m 안의 정점만(날개 끝 제외)
      const n = mesh.geometry.getAttribute('position').count;
      for (let i = 0; i < n; i++) {
        mesh.getVertexPosition(i, v).applyMatrix4(mesh.matrixWorld);
        if (v.distanceToSquared(hp) > R2) continue;
        if (!best || v.y > best.y) best = (best ?? new THREE.Vector3()).copy(v);
      }
      const pr = (p: THREE.Vector3) => { const q = projectToScreen([p.x, p.y, p.z], cam.matrixWorldInverse.elements, cam.projectionMatrix.elements, rc.width, rc.height); return q ? { x: Math.round(q.x + rc.left), y: Math.round(q.y + rc.top) } : null; };
      return { head: pr(hp), horn: best ? pr(best) : null, headWorld: { x: +hp.x.toFixed(2), y: +hp.y.toFixed(2), z: +hp.z.toFixed(2) }, hornWorld: best ? { x: +best.x.toFixed(2), y: +best.y.toFixed(2), z: +best.z.toFixed(2) } : null, viewport: { w: Math.round(rc.width), h: Math.round(rc.height) } };
    },
    /** QA: 후처리(블룸·틴트 원인 분리용 프레임 캡처에서 블룸 세기·임계를 바꾼다) */
    post,
    /** QA: 색 대비 원인 분리(요소를 하나씩 끄는 프레임 캡처)용 장면·조명·배우 참조 */
    three: { THREE, renderer, scene, hemi, sun, mobs, boss },
    mmo: () => ({ visible: fieldMmo.group.visible, drawn: fieldMmo.drawn, skinned: fieldMmo.peers.skinned }),
    party: () => ({ visible: partyPeers.group.visible, drawn: partyPeers.drawn, skinned: partyPeers.peers.skinned }),
    raidPeers: () => ({ visible: raidPeers.group.visible, drawn: raidPeers.drawn, skinned: raidPeers.peers.skinned }),
  };
  if (debugOn) (window as unknown as { __DHR__: unknown }).__DHR__ = dbg;
  let detachLookPanel: (() => void) | undefined;
  // 실시간 룩 조절판(?look): 개발 서버 전용. DEV가 아니면 동적 import 자체가 번들에서 빠진다
  if (import.meta.env.DEV && new URLSearchParams(location.search).has('look')) {
    void import('./env/lookPanel').then((m) => {
      if (!disposing) detachLookPanel = m.attachLookPanel({ view, MOOD, applyMood, post, rig });
    }).catch((error: unknown) => console.warn('개발용 룩 조절판을 불러오지 못함', error));
  }

  // Kael 모델: 부팅을 막지 않고 불러와 미리 컴파일한 뒤 캡슐과 바꾼다. 실패하거나 KAEL_LOAD_TIMEOUT_MS 안에 오지 않으면
  // 캡슐로 시작할 수 있게 하고 타이틀의 '다시 시도'(assetStatus.retry)로 다시 받는다. 늦게라도 먼저 도착한 모델 하나만 붙인다.
  let kaelAttached = false;
  let restStarted = false;
  const startRest = (): void => {
    if (restStarted) return;
    restStarted = true;
    if (staged) r1WarmP = warmRest(); else prepareRest();
  };
  /** 단계 부팅: 준비 신호를 내기 전에 실제 화면(마을+Kael+후처리)을 한 번 그려 첫 프레임 컴파일을 인트로 뒤에서 끝낸다. */
  let holdRender = staged;
  let framesSinceHold = 0;
  const warmFirstFrames = async (): Promise<void> => {
    if (!holdRender) return;
    holdRender = false;
    const t0 = performance.now();
    while (framesSinceHold < 2 && performance.now() - t0 < 3000) await new Promise<void>((r) => setTimeout(r, 16));
    mark('firstFrames');
  };
  const loadKael = (gltfP: ReturnType<typeof loadKaelGltf>, requestedAt: number): void => {
    const attempt = ++assetStatus.attempts;
    let failedOnce = false;
    const failKael = (err: unknown): void => {
      if (failedOnce || kaelAttached || attempt !== assetStatus.attempts) return;
      failedOnce = true;
      console.warn('Kael 모델을 불러오지 못해 캡슐로 대신한다(시도 ' + attempt + ')', err);
      mark('kaelFailed' + attempt);
      void envReady.then(async () => {
        if (kaelAttached || attempt !== assetStatus.attempts) return;
        await warmFirstFrames();
        if (kaelAttached || attempt !== assetStatus.attempts) return;
        assetStatus.failed = true;
        assetStatus.retry = () => {
          if (kaelAttached || !assetStatus.failed) return;
          assetStatus.failed = false;
          assetStatus.retry = null;
          stage.kael = false;
          pushProgress(0);
          loadKael(loadKaelGltf((p) => pushProgress(p), assetStatus.attempts + 1), performance.now());
        };
        mark('start');
        startRest();
      });
    };
    // 기한은 받기·모델 생성·미리 컴파일까지(모델을 실제로 붙일 수 있을 때까지) 적용한다
    // 첫 시도는 렌더러 준비보다 먼저 요청하므로 기한은 요청 시각부터 센다(렌더러 준비 시간도 기한에 들어간다).
    const remaining = Math.max(0, KAEL_LOAD_TIMEOUT_MS - (performance.now() - requestedAt));
    const watchdog = setTimeout(() => failKael(new Error('Kael 모델 준비 시간 초과 ' + KAEL_LOAD_TIMEOUT_MS + 'ms')), remaining);
    gltfP
      .then(async (gltf) => {
        if (kaelAttached) { clearTimeout(watchdog); return; }
        const kael = new KaelModel(gltf, hero.rim, q.shadowMap > 0);
        try {
          await compileAsync(kael.root, rig.camera, scene);
        } catch (err) {
          console.warn('Kael 미리 컴파일 실패(첫 프레임에 컴파일된다)', err);
        }
        clearTimeout(watchdog);
        // 다른 시도가 먼저 붙었으면 늦게 끝난 모델 자원을 해제한다
        if (kaelAttached) { kael.dispose(); return; }
        kaelAttached = true;
        kaelBody = kael;
        hero.attachModel(kael);
        // 천공 올려베기 전용 클립은 몸을 붙인 뒤 따로 읽는다(첫 화면을 늦추지 않게, 실패하면 초승달 휘두름 대체)
        void loadExtraClips(KAEL_LAUNCHER_URL).then((clips) => kael.addClips(clips));
        mark('kaelReady');
        stage.kael = true;
        pushProgress();
        await envReady;
        await warmFirstFrames();
        assetStatus.progress = 1;
        assetStatus.failed = false;
        assetStatus.retry = null;
        assetStatus.ready = true;
        mark('start');
        startRest();
        if (debugOn) console.info('Kael 클립 타이밍 ' + JSON.stringify(kael.table));
      })
      .catch((err: unknown) => { clearTimeout(watchdog); failKael(err); });
  };
  loadKael(kaelGltf, kaelRequestedAt);

  /** 전투 연출 컷(COMBAT-FEEL 7.1): 게임 이벤트에 맞춰 카메라 컷을 건다. 우선순위·강도는 CineDirector가 정한다 */
  const cueCamera = (e: GameEvent): void => {
    switch (e.type) {
      case Ev.BossIntro: rig.cue('bossIntro', e.x, e.z); break;
      case Ev.BossDefeated: rig.cue('bossDown', e.x, e.z); break;
      case Ev.AwakenPhase:
        if (e.a === AwakenPhase.Gather) rig.cue('awakenGather');
        else if (e.a === AwakenPhase.Barrage) rig.cue('awakenBurst');
        break;
      case Ev.SkillCast:
        // 라이라: 멀리 놓는 스킬(화살비 5·폭발 8·회오리 9)만 앞으로 시선을 조금 옮긴다(카엘 돌진·폭풍·도약 컷을 쓰지 않는다)
        if (view.heroId === 'lyra') {
          if (e.a === 5 || e.a === 8 || e.a === 9) rig.cue('skillCrescent', e.x + Math.sin(e.b) * 6, e.z + Math.cos(e.b) * 6);
          break;
        }
        // 스킬 4 진홍 초승달: 전방 6m 지점으로 시선만 살짝(초승달이 나가는 길이 화면에 들어오게)
        // C9: 7 대지 가르기는 균열이 나가는 전방 5m, 6 공중 난무는 올려베기 컷(공중 높이를 따라감), 8 혈검 회전베기는 폭풍 컷
        if (e.a === 4) rig.cue('skillCrescent', e.x + Math.sin(e.b) * 6, e.z + Math.cos(e.b) * 6);
        else if (e.a === 7) rig.cue('skillCrescent', e.x + Math.sin(e.b) * 5, e.z + Math.cos(e.b) * 5);
        else rig.cue(e.a === 1 ? 'skillDash' : e.a === 2 || e.a === 8 ? 'skillStorm' : e.a === 5 || e.a === 6 ? 'skillLauncher' : 'skillLeap');
        break;
      case Ev.MassKill: rig.cue('massKill'); break;
      case Ev.WaveStart: rig.cue('wave'); break;
      case Ev.RoomClear: rig.cue('roomClear'); break;
      case Ev.JobChange: if (e.c === 1) rig.cue('jobChange'); break;
    }
  };

  const onEvent = (e: GameEvent): void => {
    rig.onCombatEvent(e, view.settings.reduceFlash);
    const now = clocks.heroAt(e.tick);
    const mature = view.settings.mature;
    cueCamera(e);
    eruptions.pool.onEvent(e, clocks.worldNow);
    regionFx.onEvent(e, view.map.id);
    specialFx.onEvent(e);
    bossRageFx.onEvent(e);
    raidVfx.onEvent(e, view);
    hellFx.onEvent(e);
    fieldRender.onEvent(e, view);
    if (view.heroId === 'lyra') {
      const lag = Math.max(0, clocks.heroNow - now) / 60;
      // 라이라 스킬 카메라 큐(타임라인 camera 채널): 카엘과 같은 이름으로 짧은 반동만
      if (e.type === Ev.SkillCue && e.b === 2) {
        const tl = heroSkills('lyra', (e.flags & 2) !== 0 ? 1 : 0)[(e.a | 0) - 1]; // 갈래 1 = 트랩 레인저(C10-6)
        const name = tl?.cues[e.c | 0]?.name;
        const k = view.settings.reduceFlash ? 0.35 : 1;
        if (name === 'punch_heavy') rig.punch(5 * k, 140, Math.sin(e.d), Math.cos(e.d));
        else if (name === 'punch_mid') rig.punch(3 * k, 110, Math.sin(e.d), Math.cos(e.d));
      }
      if (lyraLink.onEvent(e, lag, hero.x, hero.y, hero.z, view.hero.facing)) return;
    }
    switch (e.type) {
      case Ev.Slash:
        combatFx.armStar();
        pushArmed = true;
        if (hero.model && e.b >= 0 && e.b < 4 && hero.model.attacks[e.b]) {
          const a = hero.model.attacks[e.b];
          slashes.onTrail(a.tip, a.base, e.x, view.hero.y, e.z, e.a, KAEL_SCALE, a.headStart, a.drawTicks, (e.flags & 1) !== 0, e.c > 0 ? e.c * 1.05 : 3.8, now, e.b);
          combatFx.noteTrail(e.tick, e.b, e.x, e.z, a.drawTicks, TRAIL_TICKS);
        } else slashes.onSlash(e, now);
        break;
      case Ev.Hit: {
        damage.onEvent(e);
        combatFx.onEvent(e, now, mature, hero.x, hero.z);
        hitStates.onEvent(e);
        // 결투장식 푸시인: 동작의 첫 타격이 마무리타·스킬이면(flags 2·4) 짧게 확대한다. 각성(8)·다단 중간타(16)는 대기를 남긴다
        if (pushArmed && e.b !== 2 && (e.flags & 24) === 0) {
          pushArmed = false;
          if ((e.flags & 6) !== 0) rig.pushIn(PUSH_IN.hit[0], PUSH_IN.hit[1], PUSH_IN.hit[2]);
        }
        break;
      }
      case Ev.Ko:
        // 결투장식 KO: 화면 어둡게(설정 '전투 연출' 배율) + 큰 푸시인 + 흰 별·붉은 X(CombatFx)
        koScreen.trigger(clocks.realMs, rig.cine.intensity);
        rig.pushIn(PUSH_IN.ko[0], PUSH_IN.ko[1], PUSH_IN.ko[2]);
        combatFx.onEvent(e, now, mature, hero.x, hero.z);
        break;
      case Ev.AwakenPhase:
      case Ev.AwakenSlash:
        awaken.onEvent(e, clocks);
        break;
      case Ev.SkillCue:
        // 스킬 타임라인 큐: 이름으로 모듈 조합을 고른다(vfx 채널만). 이벤트가 늦게 도착한 만큼 따라잡는다
        // 높이는 큐가 난 틱의 영웅 높이(e.y): 최신 view.hero.y를 쓰면 늦게 처리한 도약 큐의 바닥 고리가 공중에 떴다(Codex P2 c013)
        skillModules.onSkillCue(e.a, e.b, e.c, e.x, e.y, e.z, e.d, Math.max(0, clocks.heroNow - now) / 60, (e.flags & 2) !== 0 ? 1 : 0);
        break;
      case Ev.MobDown:
        hitStates.onEvent(e);
        break;
      case Ev.LootDrop:
      case Ev.LootPickup:
      case Ev.GearUnique:
      case Ev.Consume:
        // 장비 연출(c017): 줍기 순간·고유 효과 발동, 이름 있는 유니크 떨어짐(l1029, LootDrop flags & 1만 — 나머지 드롭은 gearFx가 무시). 모두 CombatFx가 다루지 않는 이벤트다
        gearFx.onEvent(e);
        break;
      case Ev.MobWake:
        // 기상 무적은 세계 틱으로 센다: 늦게 처리된 만큼(세계 시계)을 넘겨 남은 무적 시간만 오라를 띄운다(Codex P2 c013)
        hitStates.onEvent(e, Math.max(0, clocks.worldNow - clocks.worldAt(e.tick)) / 60);
        break;
      case Ev.SceneChange:
        resetScene();
        break;
      default:
        // 처치·대량 처치·회피·스킬 시전·경고 발동·보스 등장/퇴장 등 이펙트는 CombatFx가 맡는다
        combatFx.onEvent(e, now, mature, hero.x, hero.z);
        break;
    }
  };

  // 드로우콜·프레임 시간 기록(QA)
  const info = renderer.info.render as unknown as { drawCalls?: number; calls?: number; triangles?: number };
  const frameMs = new Float32Array(PERF_FRAMES);
  let frameCount = 0;
  let lastDraw = 0;
  let lastTris = 0;
  /** 직전 프레임 렌더 함수의 JS 시간(ms): 조절기의 CPU 병목 판정용 */
  let lastCpuMs = 0;
  let cursor = 0;
  let lastSerial = -1;
  /** 장식 층 상태를 맞춘 맵(환경 준비가 끝난 뒤 한 번) */
  let decorAppliedFor: MapVisual | null = null;
  // 탭 복귀 첫 프레임의 긴 간격은 기기 성능이 아니다
  const onVisibility = (): void => { if (document.visibilityState === 'visible') governor?.hold(performance.now(), 1500); };
  document.addEventListener('visibilitychange', onVisibility);

  // 카엘 x-ray: 카메라→영웅(발·가슴) 두 선분에 몹·보스가 걸릴 때만 그린다. 벽·문은 카메라 쪽 컷어웨이가 낮춘다.
  let xrayOn = false;
  let xrayUntil = 0;
  const rayHits = (cx: number, cy: number, cz: number, px: number, py: number, pz: number, mx: number, my: number, mz: number, r: number): boolean => {
    const dx = px - cx, dy = py - cy, dz = pz - cz;
    const L2 = dx * dx + dy * dy + dz * dz;
    if (L2 < 1e-6) return false;
    const t = ((mx - cx) * dx + (my - cy) * dy + (mz - cz) * dz) / L2;
    // 영웅보다 0.5m 이상 카메라 쪽에 있는 것만
    if (t <= 0 || t >= 1 - 0.5 / Math.sqrt(L2)) return false;
    const ex = cx + dx * t - mx, ey = cy + dy * t - my, ez = cz + dz * t - mz;
    return ex * ex + ey * ey + ez * ez < r * r;
  };
  const heroOccluded = (): boolean => {
    const c = rig.camera.position;
    const hx = hero.x, hz = hero.z, y0 = hero.y + 0.5, y1 = hero.y + 1.9;
    const m = view.mobs;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive) continue;
      const k = MOB_STATS[m.kind[i]];
      const s = m.elite[i] ? 1.4 : 1;
      const r = Math.max(0.7, k.radius) * s + 0.3;
      const my = m.y[i] + 0.7 * s;
      if (rayHits(c.x, c.y, c.z, hx, y0, hz, m.x[i], my, m.z[i], r) || rayHits(c.x, c.y, c.z, hx, y1, hz, m.x[i], my, m.z[i], r)) return true;
    }
    const b = view.boss;
    if (b.state !== BossState.Inactive && b.state !== BossState.Dead) {
      if (rayHits(c.x, c.y, c.z, hx, y0, hz, boss.x, 2.5, boss.z, 3.2) || rayHits(c.x, c.y, c.z, hx, y1, hz, boss.x, 2.5, boss.z, 3.2)) return true;
    }
    return false;
  };

  // 렌더 루프 감시: 여기서부터 main의 루프가 render()를 부른다. 비단계 부팅의 내려받기·전체 컴파일 시간은 세지 않는다.
  // 단계 부팅의 그리기 보류(holdRender, 마을·Kael 내려받기 대기)는 render 호출로 센다 — 느린 네트워크로 거짓 재부팅하지 않게 루프 생존만 본다.
  if (backend === 'webgpu') {
    const watch = new FirstFrameWatch(performance.now());
    firstFrameTimer = setInterval(() => {
      const r = watch.tick(performance.now(), document.visibilityState !== 'hidden', framesDrawn);
      if (r === 'wait') return;
      if (firstFrameTimer) clearInterval(firstFrameTimer);
      firstFrameTimer = null;
      if (r === 'timeout') runtimeFallback('first-frame', 'render loop never ran (loadTimes ' + JSON.stringify(loadTimes) + ')');
    }, 1000);
  }
  return {
    get kael() { return hero.model?.tierState ?? null; },
    backend,
    quality,
    choice,
    three: renderer,
    resize,
    drawCalls: () => lastDraw,
    waiting: () => {
      // 필드(레인 F): 처음 보일 청크 환경과 지역1 몹 자원
      if (view.flow.scene === Scene.Field) {
        void fieldRender.start(view.field?.activeChunks);
        return fieldRender.ready() && warm.r1 ? null : '필드를 준비하는 중…';
      }
      // 균열: 층 지도(지역1 스테이지 지도)의 환경을 기다린다
      if (view.flow.scene === Scene.Rift && view.map.stageKey) {
        const sv = ensureMap(view.map);
        void prepareEnvTracked(sv, view.map.id + view.map.stageKey);
        return envSettled.has(sv) && warm.r1 ? null : '균열을 여는 중…';
      }
      if (view.flow.scene !== Scene.Dungeon) return null;
      // R3·R4(D3a)도 시전형 보스(서리 리치·독안개 사제)가 잿불 대주술사 모델을 쓰므로 R2 자원 준비를 같이 기다린다
      const needR2 = view.map.id !== 'r1';
      // 지역1 스테이지 지도(변형): 입장할 지도의 환경 준비가 끝날 때까지 기다린다(예열 대상이 아닌 스테이지를 처음 고른 경우)
      if (view.map.stageKey && stageEntryMap(view.flow.stageId) === view.map) {
        const sv = ensureMap(view.map);
        void prepareEnvTracked(sv, view.map.id + view.map.stageKey);
        if (sv !== firstMap && !envSettled.has(sv)) return '던전을 준비하는 중…';
      }
      if (needR2 && !warm.r2) void warmR2();
      // 지역3·4 변형 몹·보스(c021)
      const look = REGION_LOOK[view.map.id];
      if (look && !lookStarted[look]) void warmLook(look);
      return warm.r1 && (!needR2 || warm.r2) && (!look || lookWarm[look]) ? null : '던전을 준비하는 중…';
    },
    warmProgress: () => warmFrac,
    damageNumbers: () => damage.activeCount,
    flashes: () => ({ granted: flash.granted, blocked: flash.blocked }),
    dispose() {
      disposing = true;
      unwatchPipelines();
      if (firstFrameTimer) clearInterval(firstFrameTimer);
      detachLookPanel?.();
      renderer.onDeviceLost = () => {};
      document.removeEventListener('visibilitychange', onVisibility);
      if (debugOn) delete (window as unknown as { __DHR__?: unknown }).__DHR__;
      awaken.dispose();
      damage.dispose();
      particles.dispose();
      regionFx.dispose();
      specialFx.dispose();
      bossRageFx.dispose();
      avatarLook.dispose();
      slashes.dispose();
      combatFx.dispose();
      skillModules.dispose();
      if (lyraLook) removeLyraLook(lyraLook);
      lyraFx.dispose();
      rootRings.dispose();
      mercs.dispose();
      raidFx.dispose();
      hellFx.dispose();
      raidVfx.dispose();
      bossPointer.dispose();
      eruptions.dispose();
      blobs.dispose();
      telegraphs.dispose();
      lootFx.dispose();
      lootLabels.dispose();
      boss.dispose();
      mobs.dispose();
      kaelBody?.dispose();
      lyraBody?.dispose();
      if (hero.model !== kaelBody && hero.model !== lyraBody) hero.model?.dispose();
      hero.dispose();
      for (const mv of mapCache.values()) mv.dispose();
      mapCache.clear();
      post.dispose();
      fxLayer.remove();
      renderer.dispose();
    },
    perf(): PerfStats {
      const n = Math.min(frameCount, PERF_FRAMES);
      const arr = Array.from(frameMs.subarray(0, n)).sort((a, b) => a - b);
      const at = (p: number) => (n > 0 ? arr[Math.min(n - 1, Math.floor(p * n))] : 0);
      const g: GovernorState | null = governor ? governor.state() : null;
      return {
        p50: at(0.5), p90: at(0.9), p95: at(0.95), drawCalls: lastDraw, triangles: lastTris, frames: frameCount,
        auto: !!governor, level: quality, step: g ? g.step : -1, label: cur.label,
        targetMs: g ? g.targetMs : 0, governorP90: g ? g.p90 : 0, changes: g ? g.changes : 0, cpuBound: g ? g.cpuBound : false,
        pixelRatio: renderer.getPixelRatio(), lodMobs: mobs.visibleLod,
      };
    },
    render(alpha: number, frameDt: number, draw = true) {
      // 렌더 루프 감시는 루프가 살아 있는지만 본다(단계 부팅의 그리기 보류도 센다 — 내려받기 대기라서)
      framesDrawn++;
      if (dbg.frozenUntil > performance.now()) return;
      // 단계 부팅: 마을·Kael이 준비되기 전에는 그리지 않는다(그레이박스 프레임과 즉석 셰이더 컴파일을 만들지 않는다)
      if (holdRender) return;
      framesSinceHold++;
      const cpuT0 = performance.now();
      frameMs[frameCount % PERF_FRAMES] = frameDt * 1000;
      frameCount++;
      // 자동 품질 조절: 시작 버튼 전(로딩·컴파일)에는 판단하지 않는다
      if (governor) {
        const nowMs = performance.now();
        if (!assetStatus.ready && !assetStatus.failed) governor.hold(nowMs, 500);
        else {
          const s = governor.push(frameDt * 1000, nowMs, lastCpuMs);
          if (s >= 0) applyStep(QUALITY_LADDER[s]);
        }
      }
      clocks.update(view, alpha, frameDt);
      shaderPin = view.qaFrame && Number.isFinite(view.worldTime) ? view.worldTime / 60 : null;

      const serial = Number.isFinite(view.flow.sceneSerial) ? view.flow.sceneSerial : 0;
      if (serial !== lastSerial) {
        lastSerial = serial;
        resetScene();
      }
      if (builtFor !== view.map && fieldRender.owns(view.map)) {
        if (activeMap) activeMap.group.visible = false;
        activeMap = null;
        builtFor = view.map;
        void fieldRender.start(view.field?.activeChunks);
        fieldRender.setVisible(true);
        applyMood(fieldRender.moodMap);
        eruptions.setTheme(undefined);
        rig.snap();
      }
      if (builtFor !== view.map) {
        fieldRender.setVisible(false);
        if (activeMap) activeMap.group.visible = false;
        activeMap = ensureMap(view.map);
        activeMap.group.visible = true;
        // 시작 버튼 전 실패 경로 등으로 아직 시작하지 않았다면 지금 시작한다(끝나는 순간 그레이박스와 바뀐다)
        void prepareEnv(activeMap, view.map.id);
        builtFor = view.map;
        applyMood(view.map);
        eruptions.setTheme(view.map.hazardTheme);
        rig.snap();
      }
      // 탑 51층 이상: 10층 구간이 바뀌면(같은 지도라 위 블록이 안 도는 경우) 분위기만 다시 섞는다
      {
        const tk = towerThemeKey(view.flow.stageId);
        if (tk !== lastTowerTheme) { lastTowerTheme = tk; if (!fieldRender.owns(view.map)) applyMood(view.map); }
      }
      // 전투 코어 VFX는 이벤트 처리 중에 현재 시각으로 섬광·효과 시작 시각을 찍는다: 이벤트를 꺼내기 전에 시각·보간값을 맞춘다
      combatFx.setView(view, alpha, clocks.realMs);
      // VFX 모듈은 이벤트보다 먼저 이 프레임만큼 진행한다(c012): 새 큐는 늦게 온 만큼(lag)만 따라잡고, 같은 프레임 update로 두 번 늙지 않는다.
      // 이전 순서(이벤트 → update)는 30fps에서 짧은 선딜 큐(0.15초)를 2틱, 10fps에서 6틱 일찍 끝냈다
      skillModules.setReduceFlash(view.settings.reduceFlash);
      skillModules.updateTicks(clocks.heroDelta);
      // c022 영웅별 연출: 화살·화살비는 모듈과 같은 순서(이벤트 전 진행, 새 화살은 늦은 만큼 lead로 따라잡는다)
      const heroNowId: 'kael' | 'lyra' = view.heroId === 'lyra' ? 'lyra' : 'kael';
      if (heroNowId !== heroShown) {
        heroShown = heroNowId;
        // 영웅 림·각성 발광 색: 라이라 청록, 카엘은 맵 분위기 색(applyMood가 정한 값)
        if (heroNowId === 'lyra') hero.rim.color.value.setHex(0x3fd0c0);
        else hero.rim.color.value.setHex(view.map.id === 'r2' ? 0xc3d9f2 : PALETTE.crimson);
        hero.glowHex = heroNowId === 'lyra' ? 0x3fd0c0 : 0xff2a3d;
      }
      combatFx.setHeroId(heroNowId);
      awaken.setHero(heroNowId);
      lyraFx.setReduceFlash(view.settings.reduceFlash);
      lyraFx.update(clocks.heroDelta / 60);
      gearFx.setReduceFlash(view.settings.reduceFlash);
      gearFx.update(clocks.heroDelta / 60);
      // 기상 오라 대상: 몹 위치를 매 프레임 따라가고 몹이 기상 상태를 벗어나면 놓는다(Codex P2 c013)
      // 기상 무적은 세계 틱으로 세므로 세계 시계로 진행한다(각성 감속 중에도 무적이 남은 동안 오라 유지, Codex P2 c013)
      hitStates.update(clocks.worldDelta / 60, view.mobs, alpha);
      cursor = view.events.readFrom(cursor, onEvent);

      hero.update(view, alpha, frameDt, clocks);
      rebirthFx.update(view, hero.x, hero.z, clocks.realMs / 1000, view.settings.reduceFlash);
      // 라이라 전용 몸: 라이라를 처음 고를 때 읽어 두고, 영웅이 바뀌면 몸을 갈아 끼운다(외형은 새 몸에 다시 붙인다)
      if (heroNowId === 'lyra' && kaelBody && lyraBodyState === 'none') {
        lyraBodyState = 'loading';
        void loadKaelGltf(() => {}, 1, LYRA_URL).then(async (g) => {
          const m = new KaelModel(g, hero.rim, q.shadowMap > 0, { tiers: false });
          try { await compileAsync(m.root, rig.camera, scene); } catch (err) { console.warn('라이라 몸 미리 컴파일 실패(첫 프레임에 컴파일된다)', err); }
          lyraBody = m;
          lyraBodyState = 'ready';
        }).catch((err: unknown) => { lyraBodyState = 'failed'; console.warn('라이라 몸을 읽지 못해 카엘 몸 + 색조 외형으로 둔다', err); });
      }
      const bodyWant = heroNowId === 'lyra' && lyraBody ? lyraBody : kaelBody;
      if (bodyWant && hero.model !== bodyWant) {
        if (lyraLook) { avatarLook.resetDye(); removeLyraLook(lyraLook); lyraLook = null; lyraLookMesh = null; lyraLookBow = 0; }
        hero.attachModel(bodyWant);
      }
      // 라이라 외형: 영웅이 라이라면 리그에 활·화살통(카엘 몸이면 색조도)을 붙이고, 단계 교체(몸 메시 교체) 뒤에는 다시 붙인다. 카엘로 돌아가면 뗀다
      const km = hero.model;
      if (km) {
        if (heroNowId === 'lyra') {
          // 장궁 모양: 장착 무기 외형 단계의 Blender 생성 GLB(heroWeapons). 읽는 동안·실패 시 절차 활
          const bowTier = (view.stats.weaponTier ?? 1) as WeaponTier;
          if (lyraBowWant !== bowTier) {
            lyraBowWant = bowTier;
            void loadWeaponGeometry('bow', bowTier).then((g) => { if (lyraBowWant === bowTier) { lyraBowGeo = g; lyraBowTier = bowTier; } }).catch((err: unknown) => console.warn('장궁 모델을 읽지 못해 절차 활로 둔다', err));
          }
          const bowHave = lyraBowGeo && lyraBowTier === bowTier ? bowTier : 0;
          if ((!lyraLook || lyraLookMesh !== km.mesh || lyraLookBow !== bowHave) && lyraLookFailed !== km.mesh) {
            try {
              const prevAim = lyraLook?.aim ?? 0; // 활 모양이 바뀌어 다시 붙여도 조준 자세가 한 프레임 튀지 않게
              avatarLook.resetDye(); // 라이라 외형이 염색 노드를 원래 노드로 잘못 저장·복원하지 않게 먼저 걷는다(다음 update가 다시 건다)
              if (lyraLook) removeLyraLook(lyraLook);
              lyraLook = applyLyraLook(km.root, { tint: km !== lyraBody, accessories: km !== lyraBody, body: km === lyraBody ? 'lyra' : 'kael', ...(bowHave ? { bowGeometry: lyraBowGeo! } : {}) });
              if (lyraLookMesh === km.mesh) setLyraAim(lyraLook, prevAim);
              lyraLookMesh = km.mesh;
              lyraLookBow = bowHave;
            } catch (err) {
              lyraLook = null;
              lyraLookFailed = km.mesh;
              console.warn('라이라 외형을 붙이지 못해 카엘 모습으로 둔다', err);
            }
          }
        } else if (lyraLook) {
          avatarLook.resetDye(); // 라이라 외형이 염색 노드를 원래 노드로 잘못 저장·복원하지 않게 먼저 걷는다(다음 update가 다시 건다)
          removeLyraLook(lyraLook);
          lyraLook = null; lyraLookMesh = null; lyraLookBow = 0;
        }
      }
      // 아바타 외형(레인 E): 라이라 외형 뒤에 붙인다(몸 재질 염색이 라이라 색조 위에 얹히게)
      avatarLook.update(view.avatar, heroNowId, km, hero.groundPosition, frameDt, km && km === lyraBody ? 'lyra' : 'kael');
      const hv = view.hero;
      if (heroNowId === 'lyra') {
        // 시위: 공격·스킬 중에는 당기고 화살이 나가면 놓는다(lyraLink.draw), 그 밖에는 풀린다
        const aiming = hv.act === HeroAct.Attack || hv.act === HeroAct.Skill || view.awaken.phase !== AwakenPhase.None;
        const d = lyraLink.draw.update(clocks.heroDelta / 60, aiming);
        if (lyraLook) {
          setLyraDraw(lyraLook, d);
          // 활 소켓: 쏘는 동안은 조준 회전, 그 밖에는 들기 회전(라이라 몸만 두 값이 다르다). 0.1초에 올리고 0.2초에 내린다
          const aimW = lyraLook.aim, rate = aiming ? 10 : 5;
          setLyraAim(lyraLook, aimW + ((aiming ? 1 : 0) - aimW) * (1 - Math.exp(-rate * clocks.heroDelta / 60)));
        }
        // 스킬이 길이를 다 채우기 전에 다른 동작으로 넘어가면(회피·피격) 아직 안 떨어진 그 스킬 화살비를 지운다
        if (lastAct === HeroAct.Skill && (hv.act !== HeroAct.Skill || hv.skillId !== lastSkill)) {
          const tl = heroSkills('lyra', view.job?.branch === 1 ? 1 : 0)[lastSkill - 1];
          if (tl && lastActTick < tl.duration - 1) lyraFx.cutSkill(lastSkill);
        }
      }
      lastAct = hv.act; lastSkill = hv.skillId; lastActTick = hv.actTick;
      rootRings.setReduceFlash(view.settings.reduceFlash);
      rootRings.update(view, clocks.worldNow / 60);
      // 줍기 줄기·강철 거품이 쫓는 영웅 핸들(다음 모듈 갱신부터 이 위치를 쓴다)
      gearFx.setHero(hero.x, hero.y, hero.z);
      hero.root.visible = view.flow.scene !== Scene.Title;
      boss.update(view, alpha, frameDt, clocks);
      if (view.raid && view.raid.active && !raidWarm) {
        raidWarm = true;
        raidVfx.prewarm(true);
        void compileAsync(raidVfx.group, rig.camera, scene).catch((err: unknown) => console.warn('레이드 VFX 미리 컴파일 실패', err)).finally(() => raidVfx.prewarm(false));
        void mercs.load().then(async () => {
          if (!mercs.ready) return;
          mercs.prewarm(true);
          try { await compileAsync(mercs.group, rig.camera, scene); } catch (err) { console.warn('용병 미리 컴파일 실패', err); }
        });
      }
      mercs.update(view, alpha, frameDt, clocks);
      raidFx.update(view, alpha, clocks.worldNow / 60);
      hellFx.setReduceFlash(view.settings.reduceFlash);
      hellFx.update(view, clocks.worldDelta / 60, rig.camera, clocks.worldNow / 60);
      // 카메라는 영웅·보스 위치가 정해진 직후 갱신한다: 몹 빌보드·문 컷·VFX가 같은 프레임의 카메라 방향(사용자 회전 포함)을 쓴다
      const bs = view.boss.state;
      // 클리어 연출 대기(마지막 적이 쓰러지는 동안)에는 보스가 Dead여도 처치 위치를 계속 잡는다
      const clearHolding = combatScene(view.flow.scene) && view.flow.clearHold > 0;
      // 레이드 비룡은 프레이밍 상자를 키운다. C8-3 Tripo 모델(배율 1.15)은 쉴 때 뿔 끝 약 10.8m·포효 때 더 높다.
      // 키 전부를 담으면 너무 멀어져 보스가 작아진다(L11 비평 4회차) → 머리·뿔 대부분을 담는 9.6m, 몸 반경 4m(C8-4, 예전 1.4배 스크립트 모델은 8.2·3.6)
      // C8-5(블라인드 P1 '보스가 작고 배경 기둥에 묻힘'): 레이드는 하한 거리 28→RAID_FRAME.dist, 피치를 조금 눕혀 비룡이 화면을 채우게 한다.
      // 보스·영웅 몸 경계(fit·안전 상자)는 그대로라 둘 다 화면 안에 남는다
      // 비행(2페이즈·브레스) 중에는 몸이 boss.height만큼 떠 있다: 키에 더해 머리가 상단 HUD(보스 체력바) 뒤로 잘리지 않게(C8-5 G 장면)
      if (view.raid && view.raid.active) rig.setBossSize(9.6 + boss.height, 4, RAID_FRAME.dist, RAID_FRAME.pitch, RAID_FRAME.lookY); else rig.setBossSize(FIELD_BOSS_FRAME.h, FIELD_BOSS_FRAME.r);
      rig.setBossFocus(combatScene(view.flow.scene) && ((bs !== BossState.Inactive && bs !== BossState.Dead) || clearHolding), clearHolding ? view.flow.clearFocusX : boss.x, clearHolding ? view.flow.clearFocusZ : boss.z);
      rig.update(hero.x, hero.z, frameDt, view.hitstop > 0 || view.heroRate === 0);
      bossPointer.update(rig.camera, !view.flow.paused && combatScene(view.flow.scene) && bs !== BossState.Inactive && bs !== BossState.Dead, rig.bossClose, boss.x, 3.5 + boss.height, boss.z, opts.reserve ?? null, height);
      // 대기 화면을 거치지 않는 부팅(픽스처·QA)도 지역3·4에 들어가면 변형을 뒤에서 준비한다(끝나기 전에는 색조판)
      const regionLook = REGION_LOOK[view.map.id];
      if (regionLook && !lookStarted[regionLook]) void warmLook(regionLook);
      mobs.update(view, alpha, clocks, rig.camera.position);
      rig.camera.getWorldDirection(TELE_FWD);
      // VFX 모듈: 카메라를 향해 펼치는 속도선·오라 옆면 흐림의 기준 시선(다음 프레임 갱신부터 쓴다, c013)
      skillModules.setViewDir(TELE_FWD.x, TELE_FWD.y, TELE_FWD.z);
      telegraphs.setOccluder(hero.x, hero.z, TELE_FWD.x, TELE_FWD.z);
      telegraphs.update(view, alpha, clocks);
      autoRouteFx.update(clocks.realMs, hero.x, hero.z, hero.groundPosition.y);
      lootFx.update(view, clocks.realMs);
      if (blobs.mesh.visible) {
        const ground = hero.groundPosition;
        // 접지용 모델 Y 보정은 부양 높이가 아니다. 기존 실제 점프 높이로만 그림자를 감쇠한다.
        blobs.update(view, ground.x, hero.y, ground.z, boss.x, boss.z);
      }

      // 카메라 쪽 컷어웨이: 영웅보다 남쪽(카메라 쪽) 벽·랜드마크는 셰이더가, 문은 여기서 낮춘다
      setCutawayFocus(hero.x, hero.z, view.flow.scene !== Scene.Title);
      if (activeMap) {
        const gm = activeMap.gateMeshes;
        const wh = view.map.wallHeight;
        for (let i = 0; i < gm.length; i++) {
          const open = view.flow.gateOpen[i];
          const k = cutFactor(gm[i].position.x, gm[i].position.z, hero.x, hero.z);
          const hgt = Math.min(wh * (1 - open), wh + (CUT_H - wh) * k);
          gm[i].visible = open < 0.99;
          gm[i].scale.y = Math.max(0.01, hgt / wh);
          gm[i].position.y = hgt / 2;
        }
      }

      awaken.update(alpha, clocks, frameDt);
      koScreen.update(clocks.realMs);
      hero.rimBoost = awaken.rimBoost;
      slashes.update(clocks.heroNow);
      particles.update(clocks.heroDelta, view.settings.mature);
      regionFx.setReduceFlash(view.settings.reduceFlash);
      regionFx.update(view, clocks.worldDelta / 60, rig.camera, clocks.worldNow / 60);
      specialFx.setReduceFlash(view.settings.reduceFlash);
      specialFx.update(view, clocks.worldDelta / 60, rig.camera, clocks.worldNow / 60, width, height);
      // 보이는 DOM 패널 사각형은 프레임당 한 번만 읽어 이름표 두 곳(바닥 드롭·월드 시설)이 같이 쓴다
      const reservedRects = panelRects(clocks.realMs);
      lootLabels.update(view, rig.camera, width, height, reservedRects, clocks.realMs);
      bossRageFx.setReduceFlash(view.settings.reduceFlash);
      bossRageFx.update(view, clocks.worldDelta / 60, rig.camera, clocks.worldNow / 60);
      raidVfx.setReduceFlash(view.settings.reduceFlash);
      raidVfx.update(view, alpha, clocks.worldDelta / 60, rig.camera, clocks.worldNow / 60, boss);
      eruptions.update(clocks.worldNow);
      combatFx.setReduceFlash(view.settings.reduceFlash);
      combatFx.setHero(hero.x, hero.y, hero.z);
      combatFx.setHeroSkill(view.hero.skillId, view.tick);
      combatFx.update(clocks.heroNow, clocks.heroDelta, rig.camera, view.settings.mature);

      if (activeMap) activeMap.update(rig.camera);
      fieldRender.update(view, clocks.realMs);
      fieldMmo.update(view); // 다른 플레이어 보간은 FieldNet 시계(performance.now)로 — clocks.realMs가 아니다
      partyPeers.update(view);
      raidPeers.update(view);
      // 월드 이름표: 카메라 쪽 시설 이름표 흐리기 + 보이는 DOM 패널 밑으로 들어간 이름표 옆으로 비키기(env/label)
      placeLabels(rig.camera, width, height, hero.x, hero.z, reservedRects);
      // 환경 아트가 막 준비됐으면 장식 층 상태를 맞춘다(낮은 단계에서 숨김)
      if (activeMap && decorAppliedFor !== activeMap && activeMap.envState === 'ready') {
        decorAppliedFor = activeMap;
        applyDecor();
      }
      if (hero.model) {
        const nowMs = performance.now();
        if (view.flow.scene !== Scene.Title && heroOccluded()) xrayUntil = nowMs + 200;
        xrayOn = nowMs < xrayUntil;
        hero.model.setDrawCost(kaelOutline, xrayOn);
      }
      const md = moodOf(view.map);
      sun.position.set(rig.targetX + md.sunX, md.sunY, rig.targetZ + md.sunZ);
      sun.target.position.set(rig.targetX, 0, rig.targetZ);

      if (!draw) { damage.update(frameDt * 1000, rig.camera, width, height); return; }
      renderer.info.reset();
      post.render();
      lastDraw = info.drawCalls ?? info.calls ?? 0;
      lastTris = info.triangles ?? 0;
      lastCpuMs = performance.now() - cpuT0;
      if (dbg.when || dbg.watches.length > 0) {
        const d: DebugInfo = { flash: post.flash.value, granted: flash.granted };
        const h = view.hero;
        const snap = (shot: boolean): DebugHit => ({ tick: view.tick, act: h.act, step: h.comboStep, phase: h.attackPhase, actTick: h.actTick, awaken: view.awaken.phase, flash: d.flash, granted: d.granted, shot: shot ? canvas.toDataURL('image/png') : '' });
        for (const w of dbg.watches) if (!w.hit && w.when(view, d)) w.hit = snap(true);
        if (dbg.when && dbg.when(view, d)) {
          dbg.hits.push(snap(dbg.capture));
          dbg.when = null;
          dbg.frozenUntil = performance.now() + dbg.ms;
        }
      }
      damage.update(frameDt * 1000, rig.camera, width, height);
    },
  };
}

/** 문서 대조용: 드로우콜 예산 */
export const DRAW_CALL_BUDGET = BUDGET.drawCalls;
